import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Archive,
  ArchiveRestore,
  ArrowDownLeft,
  ArrowDownRight,
  ArrowLeftRight,
  ArrowUpRight,
  BadgeCheck,
  CalendarDays,
  Check,
  ChevronDown,
  CircleDollarSign,
  Copy,
  Crown,
  ChevronLeft,
  ChevronRight,
  Cloud,
  Gauge,
  Layers3,
  ListFilter,
  MessageCircle,
  Pencil,
  Plus,
  Repeat2,
  RotateCcw,
  Search,
  Settings2,
  ShieldCheck,
  Smartphone,
  Trash2,
  UserRound,
  UserRoundPlus,
  Users,
  WalletCards,
  X,
  type LucideIcon,
} from 'lucide-react';
import {
  createElement,
  useCallback,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
} from 'react';
import { useForm } from 'react-hook-form';
import {
  Link,
  Navigate,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import { z } from 'zod';

import { ApiError } from '../api/client';
import type {
  Account,
  Category,
  CategoryLimit,
  Dashboard,
  Envelope,
  FinanceSnapshot,
  Member,
  Operation,
  ReportOverview,
  HistoryEnvelope,
  HistoryEntry,
  Session,
  Workspace,
} from '../api/contract';
import { useAccountApi, useAppData } from '../app/AppContext';
import { formatMoney, rublesToMinor, todayKey } from '../lib/money';
import {
  loadPreferences,
  savePreferences,
  type DashboardPreferences,
} from '../lib/preferences';
import { canManageFinance } from '../lib/permissions';
import { Dialog } from '../shared/ui/Dialog';
import { FinanceBubble } from '../shared/ui/FinanceBubble';
import { MoneyText } from '../shared/ui/MoneyText';
import { FinanceAppearanceEditor } from '../shared/ui/FinanceAppearanceEditor';
import { FinanceAppearancePicker, FinanceNameField, FinanceSaveButton } from '../shared/ui/FinanceFormFields';
import { OperationCoin, OperationDetails, OperationSelectionCard } from '../shared/ui/OperationFields';
import { MoneyAmountInput } from '../shared/ui/MoneyAmountInput';
import {
  getFinanceIcon,
  normalizeFinanceIconId,
} from '../shared/ui/financeAppearance';
import {
  formatReportPeriodTitle,
  reportBucketLabel,
  reportChartTitle,
  reportComparison,
  reportComparisonTone,
  type ReportPeriod,
} from './report-format';
import {
  buildBudgetUpdatePlan,
  buildCategoryUpdateBody,
  structureFormAmountMinor,
  type StructureFormDirty,
} from './structure-form-model';
import {
  CompactFinanceAddButton,
  DashboardBudgetSection,
  isCompatibleDashboardDrop,
  shouldUseCompactDashboardAdd,
  type DashboardDragPayload,
} from './dashboard-budget-section';
import { useSnapshot } from './finance-hooks';
import { HISTORY_SEARCH_MAX_LENGTH, normalizeActivityQuery, safeActivityReturnTo, useActivityState } from './activity-state';
import { debtMovementLabel, minorToMoneyInput } from './debt-model';
import { DebtArchiveList } from './debts';
import './pages-parity.css';

export {
  isCompatibleDashboardDrop,
  shouldUseCompactDashboardAdd,
  type DashboardDragPayload,
} from './dashboard-budget-section';

type Period = ReportPeriod;
type OperationDraft = {
  type: 'INCOME' | 'EXPENSE' | 'TRANSFER';
  accountId?: string;
  targetAccountId?: string;
  categoryId?: string;
};
const periods: { value: Period; label: string }[] = [
  { value: 'DAY', label: 'День' },
  { value: 'WEEK', label: 'Неделя' },
  { value: 'MONTH', label: 'Месяц' },
  { value: 'YEAR', label: 'Год' },
];
export function DashboardPage() {
  const { apiGet } = useAccountApi();
  const { user, workspace } = useAppData();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const online = useOnlineStatus();
  const [period, setPeriod] = useState<Period>('MONTH');
  const [anchor, setAnchor] = useState(todayKey);
  const [dragPayload, setDragPayload] = useState<DashboardDragPayload>();
  const [contextPayload, setContextPayload] = useState<DashboardDragPayload>();
  const overlay = searchParams.get('overlay');
  const showPreferences = overlay === 'dashboard-settings';
  const showPeriod = overlay === 'period';
  const showExpenseSort = overlay === 'expense-sort';
  const [collapsedSections, setCollapsedSections] = useState({
    income: false,
    budgets: false,
    expense: false,
  });
  const [preferences, setPreferences] = useState<DashboardPreferences>(() =>
    workspace === undefined
      ? { hiddenCategoryIds: [], hiddenBudgetIds: [], expenseSort: 'default' }
      : loadPreferences(user.id, workspace.id),
  );
  const snapshotQuery = useSnapshot();
  const dashboardQuery = useQuery({
    queryKey: ['dashboard', workspace?.id, period, anchor],
    enabled: workspace !== undefined,
    queryFn: () =>
      apiGet<Envelope<Dashboard>>(
        `/workspaces/${workspace!.id}/dashboard?period=${period}&anchor=${anchor}`,
      ).then(result => result.data),
  });
  const openOverlay = useCallback(
    (name: 'dashboard-settings' | 'period' | 'expense-sort') => {
      const next = new URLSearchParams(searchParams);
      next.set('overlay', name);
      setSearchParams(next);
    },
    [searchParams, setSearchParams],
  );
  const closeOverlay = useCallback(() => {
    const next = new URLSearchParams(searchParams);
    next.delete('overlay');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);
  useEffect(() => {
    const openPreferences = () => openOverlay('dashboard-settings');
    const openPeriod = () => openOverlay('period');
    window.addEventListener('kazna:dashboard-settings', openPreferences);
    window.addEventListener('kazna:dashboard-period', openPeriod);
    return () => {
      window.removeEventListener('kazna:dashboard-settings', openPreferences);
      window.removeEventListener('kazna:dashboard-period', openPeriod);
    };
  }, [openOverlay]);
  useEffect(() => {
    const date = new Date(`${anchor}T12:00:00`);
    const title =
      period === 'MONTH'
        ? date.toLocaleDateString('ru-RU', { month: 'long' })
        : period === 'YEAR'
          ? date.toLocaleDateString('ru-RU', { year: 'numeric' })
          : formatPeriodTitle(anchor, period);
    window.dispatchEvent(
      new CustomEvent('kazna:dashboard-title', {
        detail: { title },
      }),
    );
  }, [anchor, period]);
  const snapshot = snapshotQuery.data?.snapshot;
  const dashboard = dashboardQuery.data;
  if (!online) return <ReadError />;
  if (snapshotQuery.isPending || dashboardQuery.isPending) return <Loading />;
  if (snapshot === undefined || dashboard === undefined) return <ReadError />;

  const budgetById = new Map(snapshot.budgets.map(item => [item.id, item]));
  const categoryById = new Map(snapshot.categories.map(item => [item.id, item]));
  const budgets = dashboard.budgetSummaries.filter(
    item => !preferences.hiddenBudgetIds.includes(item.budgetId),
  );
  const expenses = dashboard.categorySummaries
    .filter(item => !preferences.hiddenCategoryIds.includes(item.categoryId))
    .sort((left, right) => {
      if (preferences.expenseSort === 'name') {
        return (categoryById.get(left.categoryId)?.name ?? '').localeCompare(
          categoryById.get(right.categoryId)?.name ?? '',
          'ru',
        );
      }
      if (preferences.expenseSort === 'spent') {
        return right.spentMinor - left.spentMinor;
      }
      if (preferences.expenseSort === 'limit') {
        return right.limitMinor - left.limitMinor;
      }
      if (preferences.expenseSort === 'progress') {
        return right.progress - left.progress;
      }
      return 0;
    });
  const incomes = snapshot.categories
    .filter(
      item =>
        item.type === 'income' &&
        !item.system &&
        !item.parentId &&
        !item.archivedAt &&
        !preferences.hiddenCategoryIds.includes(item.id),
    )
    .map(item => ({ item, amount: dashboard.amountByCategory[item.id] ?? 0 }));
  const incomeTotal = incomes
    .filter(({ item }) => item.includeInTotal !== false)
    .reduce((sum, item) => sum + item.amount, 0);
  const expenseTotal = expenses
    .filter(item => categoryById.get(item.categoryId)?.includeInTotal !== false)
    .reduce((sum, item) => sum + item.spentMinor, 0);
  const useCompactIncomeAdd = shouldUseCompactDashboardAdd(incomes.length);
  const useCompactExpenseAdd = shouldUseCompactDashboardAdd(expenses.length);

  const updatePreferences = (next: DashboardPreferences) => {
    setPreferences(next);
    savePreferences(user.id, workspace!.id, next);
  };
  const canManageStructure = canManageFinance(
    workspace!.currentMembership.role,
  );
  const openOperation = (
    draft: OperationDraft,
    allowTransfer = false,
  ) => {
    const params = new URLSearchParams({
      type: draft.type,
      returnTo: 'dashboard',
    });
    if (draft.accountId) params.set('accountId', draft.accountId);
    if (draft.targetAccountId) {
      params.set('targetAccountId', draft.targetAccountId);
    }
    if (draft.categoryId) params.set('categoryId', draft.categoryId);
    if (allowTransfer) params.set('allowTransfer', '1');
    navigate(`/operations/new?${params.toString()}`);
  };

  const completeDrop = (payload: DashboardDragPayload, targetId: string) => {
    setDragPayload(undefined);
    const [targetKind, targetValue] = targetId.split(':');
    if (payload.kind === 'income' && targetKind === 'budget') {
      openOperation({
        type: 'INCOME',
        categoryId: payload.categoryId,
        accountId: targetValue,
      });
      return;
    }
    if (payload.kind === 'budget' && targetKind === 'expense') {
      openOperation({
        type: 'EXPENSE',
        categoryId: targetValue,
        accountId: payload.budgetId,
      });
      return;
    }
    if (
      payload.kind === 'budget' &&
      targetKind === 'budget' &&
      targetValue !== payload.budgetId
    ) {
      openOperation({
        type: 'TRANSFER',
        accountId: payload.budgetId,
        targetAccountId: targetValue,
      });
    }
  };

  return (
    <>
      <section className="toolbar">
        <div className="segmented" aria-label="Период">
          {periods.map(item => (
            <button
              key={item.value}
              className={period === item.value ? 'active' : ''}
              onClick={() => setPeriod(item.value)}
            >
              {item.label}
            </button>
          ))}
        </div>
        <label className="date-field">
          <span className="sr-only">Опорная дата</span>
          <input type="date" value={anchor} onChange={event => setAnchor(event.target.value)} />
        </label>
        <button className="icon-button" aria-label="Настройки панели" onClick={() => openOverlay('dashboard-settings')}>
          <Settings2 size={19} />
        </button>
        <button
          className="button button-primary"
          onClick={() => openOperation({ type: 'EXPENSE' })}
        >
          <Plus size={18} /> Операция
        </button>
      </section>

      <section className="summary-grid desktop-summary">
        <MetricCard label="В казне" value={formatMoney(dashboard.totalBudgetBalance)} />
        <MetricCard label="Доходы" value={formatMoney(dashboard.income)} tone="positive" />
        <MetricCard label="Расходы" value={formatMoney(dashboard.expense)} tone="negative" />
      </section>

      <section className="dashboard-grid">
        <article className="card finance-section income-section span-two">
          <header className="card-header finance-header">
            <button
              className="finance-section-toggle"
              aria-label={`${collapsedSections.income ? 'Развернуть' : 'Свернуть'} раздел Доходы`}
              onClick={() =>
                setCollapsedSections(value => ({ ...value, income: !value.income }))
              }
            >
              <ChevronDown className={collapsedSections.income ? 'collapsed' : ''} size={18} />
              <h2>Доходы</h2>
            </button>
            <div className="finance-amount-row">
              <MoneyText
                className="finance-section-amount"
                currencyClassName="finance-section-currency"
                value={formatMoney(incomeTotal)}
              />
              {canManageStructure && useCompactIncomeAdd && (
                <CompactFinanceAddButton
                  label="Добавить доход"
                  onClick={() =>
                    navigate('/categories/new?kind=income&returnTo=dashboard', {
                      state: { returnTo: '/dashboard' },
                    })
                  }
                />
              )}
            </div>
          </header>
          <div className="finance-bubbles" hidden={collapsedSections.income}>
            {incomes.map(({ item, amount }) => (
              <FinanceBubble
                canDrop={isCompatibleDashboardDrop}
                key={item.id}
                name={item.name}
                amount={amount}
                color={item.color}
                icon={item.icon}
                iconFallback="coins"
                mutedAmount={item.includeInTotal === false}
                dragPayload={{ kind: 'income', categoryId: item.id }}
                onDragStart={setDragPayload}
                onDragCancel={() => setDragPayload(undefined)}
                onDrop={completeDrop}
                onContextAction={setContextPayload}
                onClick={() =>
                  canManageStructure &&
                  navigate(
                    `/categories/${item.id}/edit?returnTo=dashboard`,
                    { state: { returnTo: '/dashboard' } },
                  )
                }
              />
            ))}
            {canManageStructure && !useCompactIncomeAdd && (
              <button
                className="finance-add"
                aria-label="Добавить доход"
                onClick={() =>
                  navigate('/categories/new?kind=income&returnTo=dashboard', {
                    state: { returnTo: '/dashboard' },
                  })
                }
              >
                <span className="finance-add-spacer" />
                <span className="finance-add-circle">
                  <Plus size={26} strokeWidth={2.2} />
                </span>
              </button>
            )}
          </div>
        </article>

        <DashboardBudgetSection
          budgets={budgets}
          budgetById={budgetById}
          collapsed={collapsedSections.budgets}
          canManageStructure={canManageStructure}
          dragPayload={dragPayload}
          onToggle={() =>
            setCollapsedSections(value => ({ ...value, budgets: !value.budgets }))
          }
          onDragStart={setDragPayload}
          onDragCancel={() => setDragPayload(undefined)}
          onDrop={completeDrop}
          onContextAction={setContextPayload}
          onCreate={() =>
            navigate('/budgets/new?returnTo=dashboard', {
              state: { returnTo: '/dashboard' },
            })
          }
          onEdit={budgetId =>
            navigate(`/budgets/${budgetId}/edit?returnTo=dashboard`, {
              state: { returnTo: '/dashboard' },
            })
          }
        />

        <article className="card finance-section expense-section span-two">
          <header className="card-header finance-header">
            <div className="finance-title-block">
              <div className="finance-title-row">
                <button
                  className="finance-section-toggle"
                  aria-label={`${collapsedSections.expense ? 'Развернуть' : 'Свернуть'} раздел Расходы`}
                  onClick={() =>
                    setCollapsedSections(value => ({ ...value, expense: !value.expense }))
                  }
                >
                  <ChevronDown className={collapsedSections.expense ? 'collapsed' : ''} size={18} />
                  <h2>Расходы</h2>
                </button>
                <button
                  className="finance-section-action"
                  aria-label="Сортировать расходы"
                  onClick={() => openOverlay('expense-sort')}
                >
                  <ListFilter size={17} />
                </button>
              </div>
            </div>
            <div className="finance-amount-block">
              {expenses.some(item => item.limitMinor > 0) && (
                <small>
                  лимит {formatMoney(expenses.reduce((sum, item) => sum + item.limitMinor, 0))}
                </small>
              )}
              <div className="finance-amount-row">
                <MoneyText
                  className="finance-section-amount"
                  currencyClassName="finance-section-currency"
                  value={formatMoney(expenseTotal)}
                />
                {canManageStructure && useCompactExpenseAdd && (
                  <CompactFinanceAddButton
                    label="Добавить расход"
                    onClick={() =>
                      navigate('/categories/new?kind=expense&returnTo=dashboard', {
                        state: { returnTo: '/dashboard' },
                      })
                    }
                  />
                )}
              </div>
            </div>
          </header>
          <div className="finance-bubbles expense-bubbles" hidden={collapsedSections.expense}>
            {expenses.map(item => {
              const category = categoryById.get(item.categoryId);
              return (
                <FinanceBubble
                  canDrop={isCompatibleDashboardDrop}
                  key={item.categoryId}
                  name={category?.name ?? 'Категория'}
                  amount={item.spentMinor}
                  caption={item.limitMinor > 0 ? formatMoney(item.limitMinor) : undefined}
                  color={category?.color}
                  icon={category?.icon}
                  iconFallback="shopping-bag"
                  progress={item.progress}
                  danger={item.exceeded}
                  dropTargetId={`expense:${item.categoryId}`}
                  dropTargetActive={isCompatibleDashboardDrop(
                    dragPayload,
                    `expense:${item.categoryId}`,
                  )}
                  onDrop={completeDrop}
                  onClick={() =>
                    openOperation({
                      type: 'EXPENSE',
                      categoryId: item.categoryId,
                    })
                  }
                  onLongPress={() => navigate(`/categories/${item.categoryId}`)}
                />
              );
            })}
            {canManageStructure && !useCompactExpenseAdd && (
              <button
                className="finance-add expense-add"
                aria-label="Добавить расход"
                onClick={() =>
                  navigate('/categories/new?kind=expense&returnTo=dashboard', {
                    state: { returnTo: '/dashboard' },
                  })
                }
              >
                <span className="finance-add-spacer" />
                <span className="finance-add-circle">
                  <Plus size={26} strokeWidth={2.2} />
                </span>
              </button>
            )}
          </div>
        </article>

        <article className="card recent-card">
          <header className="card-header"><div><p className="eyebrow">Последние</p><h2>Операции</h2></div></header>
          <OperationList operations={dashboard.recentOperations} categories={snapshot.categories} />
        </article>
      </section>

      {showPreferences && (
        <Dialog title="Настройка панели" onClose={closeOverlay}>
          <p className="dashboard-settings-description">
            Скрытые элементы и их суммы исчезнут только с панели. История, отчёты и
            новые операции не изменятся.
          </p>
          <DashboardVisibilitySection
            title="Доходы"
            kind="category"
            iconFallback="coins"
            items={snapshot.categories.filter(
              item =>
                item.type === 'income' &&
                !item.system &&
                !item.parentId &&
                !item.archivedAt,
            )}
            hidden={preferences.hiddenCategoryIds}
            onChange={hiddenCategoryIds =>
              updatePreferences({ ...preferences, hiddenCategoryIds })
            }
          />
          <DashboardVisibilitySection
            title="Бюджеты"
            kind="budget"
            items={snapshot.budgets.filter(item => !item.archivedAt)}
            hidden={preferences.hiddenBudgetIds}
            onChange={hiddenBudgetIds => updatePreferences({ ...preferences, hiddenBudgetIds })}
          />
          <DashboardVisibilitySection
            title="Расходы"
            kind="category"
            iconFallback="shopping-bag"
            items={snapshot.categories.filter(
              item => item.type === 'expense' && !item.system && !item.parentId && !item.archivedAt,
            )}
            hidden={preferences.hiddenCategoryIds}
            onChange={hiddenCategoryIds =>
              updatePreferences({ ...preferences, hiddenCategoryIds })
            }
          />
        </Dialog>
      )}
      {showExpenseSort && (
        <Dialog variant="sheet" title="Сортировка расходов" onClose={closeOverlay}>
          <div className="period-list">
            {[
              ['default', 'По умолчанию'],
              ['spent', 'Сначала большие расходы'],
              ['name', 'По названию'],
              ['limit', 'Сначала большие лимиты'],
              ['progress', 'По заполнению лимита'],
            ].map(([value, label]) => (
              <button
                key={value}
                onClick={() => {
                  updatePreferences({
                    ...preferences,
                    expenseSort: value as DashboardPreferences['expenseSort'],
                  });
                  closeOverlay();
                }}
              >
                <span>{label}</span>
                {preferences.expenseSort === value && <Check aria-hidden size={22} />}
              </button>
            ))}
          </div>
        </Dialog>
      )}
      {contextPayload && (
        <Dialog
          variant="sheet"
          title={
            contextPayload.kind === 'income'
              ? 'Куда направить доход'
              : 'Действие с бюджетом'
          }
          onClose={() => setContextPayload(undefined)}
        >
          <div className="mobile-choice-list dashboard-action-list">
            {contextPayload.kind === 'income' &&
              budgets.map(item => {
                const budget = budgetById.get(item.budgetId);
                const Icon = getFinanceIcon(budget?.icon);
                return (
                  <button
                    type="button"
                    key={item.budgetId}
                    onClick={() => {
                      completeDrop(contextPayload, `budget:${item.budgetId}`);
                      setContextPayload(undefined);
                    }}
                  >
                    <span style={{ background: budget?.accent }}>
                      <Icon size={22} />
                    </span>
                    <b>Пополнить «{budget?.name ?? 'Бюджет'}»</b>
                    <ChevronRight size={20} />
                  </button>
                );
              })}
            {contextPayload.kind === 'budget' && (
              <>
                {expenses.map(item => {
                  const category = categoryById.get(item.categoryId);
                  const Icon = getFinanceIcon(category?.icon, 'shopping-bag');
                  return (
                    <button
                      type="button"
                      key={`expense:${item.categoryId}`}
                      onClick={() => {
                        completeDrop(contextPayload, `expense:${item.categoryId}`);
                        setContextPayload(undefined);
                      }}
                    >
                      <span style={{ background: category?.color }}>
                        <Icon size={22} />
                      </span>
                      <b>Расход «{category?.name ?? 'Категория'}»</b>
                      <ChevronRight size={20} />
                    </button>
                  );
                })}
                {budgets
                  .filter(item => item.budgetId !== contextPayload.budgetId)
                  .map(item => {
                    const budget = budgetById.get(item.budgetId);
                    const Icon = getFinanceIcon(budget?.icon);
                    return (
                      <button
                        type="button"
                        key={`budget:${item.budgetId}`}
                        onClick={() => {
                          completeDrop(contextPayload, `budget:${item.budgetId}`);
                          setContextPayload(undefined);
                        }}
                      >
                        <span style={{ background: budget?.accent }}>
                          <Icon size={22} />
                        </span>
                        <b>Перевести в «{budget?.name ?? 'Бюджет'}»</b>
                        <ChevronRight size={20} />
                      </button>
                    );
                  })}
              </>
            )}
          </div>
        </Dialog>
      )}
      {showPeriod && (
        <Dialog variant="sheet" title="Период" onClose={closeOverlay}>
          <div className="sheet-period-navigation">
            <button
              aria-label="Предыдущий период"
              onClick={() => setAnchor(value => shiftPeriodAnchor(value, period, -1))}
            >
              <ChevronLeft size={22} />
            </button>
            <strong>{formatPeriodTitle(anchor, period)}</strong>
            <button
              aria-label="Следующий период"
              onClick={() => setAnchor(value => shiftPeriodAnchor(value, period, 1))}
            >
              <ChevronRight size={22} />
            </button>
          </div>
          <p className="sheet-section-label">Интервал</p>
          <div className="period-list">
            {periods.map(item => (
              <button
                key={item.value}
                className={period === item.value ? 'active' : ''}
                onClick={() => {
                  setPeriod(item.value);
                  closeOverlay();
                }}
              >
                <span>{item.label}</span>
                {period === item.value && <Check aria-hidden size={22} />}
              </button>
            ))}
          </div>
        </Dialog>
      )}
    </>
  );
}

export function HistoryPage() {
  const { apiGet } = useAccountApi();
  const { workspace } = useAppData();
  const navigate = useNavigate();
  const online = useOnlineStatus();
  const [searchParams] = useSearchParams();
  const snapshotQuery = useSnapshot();
  const queryClient = useQueryClient();
  const activity = useActivityState();
  const { query: search, typeFilter: type, includeCancelled, cursor, cursorHistory, period, anchor, setState } = activity;
  const [searchReady, setSearchReady] = useState(search);
  const [composing, setComposing] = useState(false);
  const searchRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (composing) return;
    const timer = window.setTimeout(() => setSearchReady(search), 300);
    return () => window.clearTimeout(timer);
  }, [search, composing]);
  const showFilters = searchParams.get('overlay') === 'history-filters';
  const closeOverlay = () => setState({}, { overlay: null, replace: true });
  const query = useQuery({
    queryKey: ['history', workspace?.id, period, anchor, search, type, includeCancelled, activity.accountId, activity.debtCategoryId, activity.authorId, activity.limit, cursor],
    enabled: workspace !== undefined && !composing && searchReady === search,
    retry: false,
    queryFn: () => {
      const params = new URLSearchParams({ period, anchor, limit: String(activity.limit), includeCancelled: String(includeCancelled) });
      const query = normalizeActivityQuery(search).trim();
      if (query) params.set('q', query);
      if (type) params.set('type', type);
      for (const key of ['accountId', 'debtCategoryId', 'authorId'] as const) if (activity[key]) params.set(key, activity[key]);
      if (cursor) params.set('cursor', cursor);
      return apiGet<HistoryEnvelope>(`/workspaces/${workspace!.id}/history?${params}`);
    },
  });
  useEffect(() => {
    if (cursor && query.error instanceof ApiError && query.error.status === 400 && query.error.code === 'INVALID_CURSOR') {
      // A revision-bound cursor is a read restart, never a replay of a command.
      // Evict all pages before returning to page one so cached page one cannot
      // be appended to, or displayed alongside, the stale revision.
      queryClient.removeQueries({ queryKey: ['history', workspace?.id] });
      setState({ cursor: undefined, cursorHistory: [] }, { replace: true });
    }
  }, [cursor, query.error, queryClient, workspace?.id, setState]);
  const snapshot = snapshotQuery.data?.snapshot;
  const entries = query.data?.data ?? [];
  const summary = query.data?.meta.fullPeriodSummary;
  const entryDate = (entry: HistoryEntry) => entry.kind === 'OPERATION' ? entry.operation.occurredAt : entry.movement.occurredAt;
  const groupedOperations = entries.reduce<Map<string, HistoryEntry[]>>((groups, entry) => {
    const key = new Date(entryDate(entry)).toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric', timeZone: workspace?.timezone });
    groups.set(key, [...(groups.get(key) ?? []), entry]);
    return groups;
  }, new Map());
  if (snapshotQuery.isPending) return <Loading />;
  if (snapshot === undefined) return <ReadError />;
  const categoryById = new Map(
    snapshot.categories.map(item => [item.id, item]),
  );
  const budgetById = new Map(snapshot.budgets.map(item => [item.id, item]));
  const memberById = new Map(snapshot.members.map(item => [item.id, item]));
  const historyDate = (value: string) => new Intl.DateTimeFormat('ru-RU', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: workspace?.timezone,
  }).format(new Date(value)).replace(',', ' ·');
  return (
    <div className="history-native-content">
      <section className="history-toolbar">
        <div className={`history-search ${search ? 'has-query' : ''}`}>
          <Search size={19} />
          <input
            ref={searchRef}
            type="search"
            aria-label="Поиск операций"
            maxLength={HISTORY_SEARCH_MAX_LENGTH}
            placeholder="Категория или примечание"
            value={search}
            onCompositionStart={() => setComposing(true)}
            onCompositionEnd={() => setComposing(false)}
            onChange={event => {
              if (!event.target.value) setSearchReady('');
              setState({ query: event.target.value }, { replace: true });
            }}
          />
          {search && <button type="button" aria-label="Очистить поиск" onClick={() => { setSearchReady(''); setState({ query: '' }, { replace: true }); searchRef.current?.focus(); }}><X size={18} /></button>}
        </div>
      </section>
      <section className="history-summary">
        <p>Итог за период</p>
        <strong>{summary ? formatMoney(summary.netMinor) : '—'}</strong>
        <div>
          <article><span>Поступления</span><b className="positive">{summary ? formatMoney(summary.incomeMinor) : '—'}</b></article>
          <article><span>Списания</span><b className="negative">{summary ? formatMoney(summary.expenseMinor) : '—'}</b></article>
        </div>
      </section>
      <section className="history-list-header">
        <h2>Список операций</h2>
        <div className="history-chips" aria-label="Тип операции">
          {[
            ['', 'Все'],
            ['EXPENSE', 'Расходы'],
            ['INCOME', 'Доходы'],
            ['TRANSFER', 'Переводы'],
          ].map(([value, label]) => (
            <button
              key={value}
              className={type === value ? 'active' : ''}
              onClick={() => {
                setState({ typeFilter: value as typeof type });
              }}
            >
              {label}
            </button>
          ))}
        </div>
        <div className="history-secondary-filters">
          <button type="button" className={`history-filter-pill ${includeCancelled ? 'active' : ''}`} aria-pressed={includeCancelled} onClick={() => setState({ includeCancelled: !includeCancelled })}>Показать отменённые</button>
        </div>
      </section>
      {!online || query.isError ? (
        <ReadError />
      ) : query.isPending ? (
        <Loading />
      ) : entries.length === 0 ? (
        <article className="history-empty">
          <Search size={28} />
          <strong>Ничего не найдено</strong>
          <span>Измените период, запрос или фильтр.</span>
        </article>
      ) : (
        <div className="operation-table history-operation-list">
          {[...groupedOperations.entries()].map(([date, items]) => (
            <section className="operation-day" key={date}>
              <header>
                <span>{date}</span>
              </header>
              <div className="history-operation-rows">
                {items.map(entry => {
                  if (entry.kind === 'DEBT_ADJUSTMENT') {
                    const movement = entry.movement;
                    const category = snapshot.debtCategories.find(c => c.id === movement.debtCategoryId);
                    const returnTo = activity.path;
                    const title = `${category ? debtMovementLabel(category.direction, movement.kind) : entry.label}${category ? ` · ${category.name}` : ''}`;
                    const author = memberById.get(movement.authorId);
                    return <button type="button" className="history-special-row" aria-label={`${title}${movement.status === 'CANCELLED' ? ', Отменено' : ''}`} key={`adjustment:${movement.id}`} onClick={() => navigate(`/debts/movements/${movement.id}?${new URLSearchParams({ returnTo })}`, { state: { returnTo } })}>
                      <strong>{title}</strong><b>{minorToMoneyInput(movement.amountMinor)} ₽</b><small>{historyDate(movement.occurredAt)} · {author?.name ?? 'Участник'}</small>{movement.note && <small>{movement.note}</small>}{movement.status === 'CANCELLED' && <small>Отменено</small>}<small>Без движения денег</small>
                    </button>;
                  }
                  const operation = entry.operation;
                  const category = categoryById.get(operation.categoryId ?? '');
                  const account = budgetById.get(
                    operation.entries[0]?.accountId ?? '',
                  );
                  const member = memberById.get(
                    operation.authorProjectionId,
                  );
                  const Icon =
                    operation.type === 'TRANSFER'
                      ? Repeat2
                      : getFinanceIcon(
                          category?.icon,
                          operation.type === 'INCOME'
                            ? 'coins'
                            : 'shopping-bag',
                        );
                  const debtCategory = snapshot.debtCategories.find(c => c.id === operation.debtId);
                  const debtMovement = snapshot.debtMovements.find(m => m.operationId === operation.id);
                  const title = debtCategory && debtMovement ? `${debtMovementLabel(debtCategory.direction, debtMovement.kind)} · ${debtCategory.name}` :
                    operation.type === 'TRANSFER'
                      ? 'Перевод между бюджетами'
                      : category?.name ?? operationLabel(operation.type);
                  if (operation.type === 'TRANSFER' || operation.debtId || operation.status === 'CANCELLED') {
                    return <button type="button" className="history-special-row" aria-label={`${title}${operation.status === 'CANCELLED' ? ', Отменено' : ''}`} key={operation.id} onClick={() => navigate(`/transactions/${operation.id}?${new URLSearchParams({ returnTo: activity.path })}`, { state: { returnTo: activity.path } })}>
                      <strong>{title}</strong><b>{minorToMoneyInput(operation.amountMinor)} ₽</b><small>{historyDate(operation.occurredAt)} · {member?.name ?? 'Участник'}</small>{operation.note && <small>{operation.note}</small>}{operation.status === 'CANCELLED' && <small>Отменено</small>}
                    </button>;
                  }
                  return (
                    <button
                      type="button"
                      className="mobile-transaction-row"
                      key={operation.id}
                      onClick={() =>
                        navigate(`/transactions/${operation.id}?${new URLSearchParams({ returnTo: activity.path })}`, {
                          state: { returnTo: activity.path },
                        })
                      }
                    >
                      <span
                        className="mobile-transaction-icon"
                        style={
                          {
                            '--category-color':
                              category?.color ?? 'var(--primary)',
                          } as CSSProperties
                        }
                      >
                        {createElement(Icon, { size: 20 })}
                      </span>
                      <span className="mobile-transaction-copy">
                        <strong>{title}</strong>
                        <small>
                          {historyDate(operation.occurredAt)}
                          {account ? ` · ${account.name}` : ''}
                          {member ? ` · ${member.name}` : ''}
                          {operation.note ? ` · ${operation.note}` : ''}
                        </small>
                      </span>
                      <strong className={operation.type === 'INCOME' ? 'positive' : ''}>
                        {operation.type === 'INCOME'
                          ? '+'
                          : operation.type === 'EXPENSE'
                            ? '−'
                            : ''}
                        {formatMoney(operation.amountMinor)}
                      </strong>
                      <ChevronRight size={16} />
                    </button>
                  );
                })}
              </div>
            </section>
          ))}
        </div>
      )}
      {(cursor || cursorHistory.length > 0 ||
        (query.data?.meta.hasMore && query.data.meta.nextCursor)) && (
        <div className="pagination history-pagination">
          <button
            className="button button-secondary"
            disabled={!cursor && cursorHistory.length === 0}
            onClick={() => {
              const previous = cursorHistory.at(-1);
              setState({ cursor: previous, cursorHistory: cursorHistory.slice(0, -1) });
            }}
          >
            {cursor && cursorHistory.length === 0 ? 'В начало' : 'Назад'}
          </button>
          <button
            className="button button-secondary"
            disabled={!query.data?.meta.hasMore || !query.data.meta.nextCursor}
            onClick={() => {
              setState({ cursor: query.data!.meta.nextCursor ?? undefined, cursorHistory: [...cursorHistory, cursor] });
            }}
          >
            Далее
          </button>
        </div>
      )}
      {showFilters && (
        <Dialog
          variant="sheet"
          title="Фильтры истории"
          onClose={closeOverlay}
        >
          <div className="period-list history-filter-list">
            {[
              ['', 'Все операции'],
              ['EXPENSE', 'Расходы'],
              ['INCOME', 'Доходы'],
              ['TRANSFER', 'Переводы'],
            ].map(([value, label]) => (
              <button
                type="button"
                key={value}
                onClick={() => {
                  setState({ typeFilter: value as typeof type }, { overlay: null, replace: true });
                }}
              >
                <span>{label}</span>
                {type === value && <Check size={21} />}
              </button>
            ))}
          </div>
          <label className="history-cancelled-filter">
            <span>
              <strong>Отменённые операции</strong>
              <small>Показывать отменённые в списке</small>
            </span>
            <input
              type="checkbox"
              checked={includeCancelled}
              onChange={event => {
                setState({ includeCancelled: event.target.checked }, { replace: true });
              }}
            />
          </label>
          <button
            type="button"
            className="button button-primary history-create-operation"
            onClick={() =>
              navigate(`/operations/new?${new URLSearchParams({ allowTransfer: '1', returnTo: activity.path })}`, { state: { returnTo: activity.path } })
            }
          >
            <Plus size={18} />
            Новая операция
          </button>
        </Dialog>
      )}
    </div>
  );
}

export function ReportsPage() {
  const { apiGet } = useAccountApi();
  const { workspace } = useAppData();
  const online = useOnlineStatus();
  const snapshotQuery = useSnapshot();
  const { period, anchor } = useActivityState();
  const reportQuery = useQuery({
    queryKey: ['report', workspace?.id, period, anchor],
    enabled: workspace !== undefined,
    queryFn: () =>
      apiGet<Envelope<ReportOverview>>(
        `/workspaces/${workspace!.id}/reports/overview?period=${period}&anchor=${anchor}`,
      ).then(result => result.data),
  });
  if (!online) return <ReadError />;
  if (reportQuery.isPending || snapshotQuery.isPending) return <Loading />;
  if (reportQuery.data === undefined || snapshotQuery.data === undefined) return <ReadError />;
  const report = reportQuery.data;
  const categoryById = new Map(snapshotQuery.data.snapshot.categories.map(item => [item.id, item]));
  const chartMax = Math.max(...report.expenseBuckets.map(item => item.expenseMinor), 1);
  const limitPercent =
    report.limits.configuredMinor > 0
      ? Math.round(
          (report.limits.spentMinor / report.limits.configuredMinor) * 100,
        )
      : undefined;
  const donutProgress = Math.max(
    0,
    Math.min(
      report.limits.configuredMinor > 0
        ? report.limits.spentMinor / report.limits.configuredMinor
        : 0,
      1,
    ),
  );
  const donutCircumference = 2 * Math.PI * 48;
  const limitColor =
    report.limits.exceededMinor > 0
      ? 'var(--negative)'
      : 'var(--primary)';
  return (
    <div className="reports-native-content">
      <section className="summary-grid report-summary">
        <article className="report-metric-card">
          <span className="report-metric-icon negative">
            <ArrowDownRight size={20} />
          </span>
          <small>Расходы</small>
          <strong>{formatMoney(report.current.expenseMinor)}</strong>
          <p className={reportComparisonTone(report.current.expenseMinor, report.previous.expenseMinor, false)}>
            {reportComparison(report.current.expenseMinor, report.previous.expenseMinor)}
          </p>
        </article>
        <article className="report-metric-card">
          <span className="report-metric-icon positive">
            <ArrowUpRight size={20} />
          </span>
          <small>Доходы</small>
          <strong>{formatMoney(report.current.incomeMinor)}</strong>
          <p className={reportComparisonTone(report.current.incomeMinor, report.previous.incomeMinor, true)}>
            {reportComparison(report.current.incomeMinor, report.previous.incomeMinor)}
          </p>
        </article>
      </section>
      <section className="report-grid">
        <article className="card span-two">
          <header className="report-card-heading">
            <h2>{reportChartTitle(period)}</h2>
            <p>{formatReportPeriodTitle(anchor, period)}</p>
          </header>
          <div className="bar-chart" role="img" aria-label="График расходов">
            {report.expenseBuckets.map(bucket => {
              const height =
                bucket.expenseMinor > 0
                  ? Math.max(
                      5,
                      Math.round(
                        (bucket.expenseMinor / chartMax) * 108,
                      ),
                    )
                  : 0;
              const isLargest =
                bucket.expenseMinor > 0 &&
                bucket.expenseMinor === chartMax;
              return (
                <div
                  className="bar-column"
                  key={bucket.from}
                  aria-label={`${reportBucketLabel(bucket.from, period, workspace?.timezone)}: ${formatMoney(bucket.expenseMinor)}`}
                >
                  <span className="bar-plot" aria-hidden>
                    {height > 0 && (
                      <span
                        className={`bar ${isLargest ? 'largest' : ''}`}
                        style={{ height: `${height}px` }}
                      />
                    )}
                  </span>
                  <small>
                    {reportBucketLabel(
                      bucket.from,
                      period,
                      workspace?.timezone,
                    )}
                  </small>
                </div>
              );
            })}
          </div>
        </article>
        <article className="card">
          <header className="report-card-heading">
            <h2>Лимиты расходов</h2>
            <p>За выбранный период</p>
          </header>
          <div className="limit-overview">
            <span className="limit-ring">
              <svg
                width="128"
                height="128"
                viewBox="0 0 128 128"
                aria-hidden
              >
                <circle
                  cx="64"
                  cy="64"
                  r="48"
                  stroke="var(--surface-muted)"
                  strokeWidth="18"
                  fill="none"
                />
                {donutProgress > 0 && (
                  <circle
                    cx="64"
                    cy="64"
                    r="48"
                    stroke={limitColor}
                    strokeWidth="18"
                    fill="none"
                    strokeDasharray={`${donutCircumference * donutProgress} ${donutCircumference}`}
                    strokeLinecap="round"
                    transform="rotate(-90 64 64)"
                  />
                )}
              </svg>
              <span>
                <strong className={report.limits.exceededMinor > 0 ? 'negative' : ''}>
                  {limitPercent === undefined ? '—' : `${limitPercent}%`}
                </strong>
                <small>{limitPercent === undefined ? 'не задано' : 'лимита'}</small>
              </span>
            </span>
            <div className="limit-overview-copy">
              <small>Общий лимит</small>
              <strong>
                {report.limits.configuredMinor > 0
                  ? formatMoney(report.limits.configuredMinor)
                  : '—'}
              </strong>
              {report.limits.configuredMinor > 0 && (
                <p className={report.limits.exceededMinor > 0 ? 'negative' : 'muted'}>
                  Потрачено по лимитам {formatMoney(report.limits.spentMinor)}
                </p>
              )}
              {report.limits.exceededMinor > 0 && (
                <p className="negative report-limit-exceeded">
                  Превышение {formatMoney(report.limits.exceededMinor)}
                </p>
              )}
            </div>
          </div>
          {report.limits.categories.length > 0 ? (
            <>
              <div className="report-divider" />
              <div className="report-limit-list">
                {report.limits.categories.map(item => {
                  const category = categoryById.get(item.categoryId);
                  const exceededBy = Math.max(
                    item.spentMinor - item.limitMinor,
                    0,
                  );
                  const percent = Math.round(item.progress * 100);
                  const color = category?.color ?? 'var(--primary)';
                  return (
                    <div
                      className="report-limit-row"
                      key={item.categoryId}
                      aria-label={`${category?.name ?? 'Категория'}: потрачено ${formatMoney(item.spentMinor)}, лимит ${formatMoney(item.limitMinor)}`}
                    >
                      <div className="report-limit-title">
                        <span
                          className="color-dot"
                          style={{ background: color }}
                        />
                        <strong>{category?.name ?? 'Категория'}</strong>
                        {category?.archivedAt && (
                          <span className="archived-badge">Архив</span>
                        )}
                        <b className={item.exceeded ? 'negative' : ''}>
                          {formatMoney(item.spentMinor)}
                        </b>
                      </div>
                      <div className="report-limit-meta">
                        <small>
                          Лимит {formatMoney(item.limitMinor)}
                        </small>
                        <small className={item.exceeded ? 'negative' : ''}>
                          {item.exceeded
                            ? `Превышение ${formatMoney(exceededBy)}`
                            : `${percent}%`}
                        </small>
                      </div>
                      <span className="report-limit-progress">
                        <i
                          style={{
                            background: item.exceeded
                              ? 'var(--negative)'
                              : color,
                            width: `${Math.min(
                              Math.max(item.progress * 100, 0),
                              100,
                            )}%`,
                          }}
                        />
                      </span>
                    </div>
                  );
                })}
              </div>
            </>
          ) : (
            <p className="report-empty">
              Лимиты на этот период не заданы
            </p>
          )}
        </article>
        <article className="card">
          <header className="report-category-heading">
            <div>
              <h2>Расходы по категориям</h2>
              <p>Всего потрачено</p>
            </div>
            <strong>{formatMoney(report.current.expenseMinor)}</strong>
          </header>
          {report.categories.length > 0 ? (
            <>
              <div className="report-divider" />
              <ol className="rank-list">
                {report.categories.slice(0, 5).map(item => {
                  const category = categoryById.get(item.categoryId);
                  return (
                    <li key={item.categoryId}>
                      <span
                        className="color-dot"
                        style={{ background: category?.color }}
                      />
                      <span>{category?.name ?? 'Категория'}</span>
                      {category?.archivedAt && (
                        <span className="archived-badge">Архив</span>
                      )}
                      <b>{formatMoney(item.amountMinor)}</b>
                    </li>
                  );
                })}
              </ol>
            </>
          ) : (
            <p className="report-empty">В этом периоде расходов нет</p>
          )}
        </article>
      </section>
    </div>
  );
}

export function ProfilePage() {
  const { apiMutation } = useAccountApi();
  const { user, workspace } = useAppData();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [name, setName] = useState(user.displayName);
  const [serverError, setServerError] = useState('');
  const [pending, setPending] = useState(false);
  const editName = searchParams.get('overlay') === 'edit-profile-name';
  const closeEdit = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('overlay');
    setSearchParams(next, { replace: true, state: location.state });
  };
  const role = workspace?.currentMembership.role;
  const roleLabel =
    role === 'OWNER'
      ? 'Владелец'
      : role === 'ADMIN'
        ? 'Администратор'
        : 'Участник';
  const save = async () => {
    if (!name.trim()) return;
    setPending(true);
    setServerError('');
    try {
      await apiMutation('/me', {
        method: 'PATCH',
        scope: 'me:update',
        version: user.version,
        body: { displayName: name.trim() },
      });
      await queryClient.invalidateQueries({ queryKey: ['me'] });
      closeEdit();
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 409) {
        await queryClient.invalidateQueries({ queryKey: ['me'] });
        setServerError(
          'Профиль изменён на другом устройстве. Данные обновлены.',
        );
      } else {
        setServerError(
          caught instanceof Error ? caught.message : 'Не удалось сохранить имя',
        );
      }
      setPending(false);
    }
  };
  return (
    <Dialog
      title="Профиль"
      onClose={() => navigate('/account', { replace: true })}
    >
      <section className="profile-page">
        <div className="profile-hero">
          <span style={{ backgroundColor: workspace?.currentMembership.color ?? 'var(--primary)' }}>{workspace?.currentMembership.initials ?? user.displayName.slice(0, 1).toUpperCase()}</span>
          <h1>{user.displayName}</h1>
          <p><BadgeCheck size={15} /> {roleLabel} пространства</p>
        </div>
        <article className="profile-info-card">
          <div>
            <span>Имя</span>
            <strong>{user.displayName}</strong>
          </div>
          <div>
            <span>Роль</span>
            <strong>{roleLabel}</strong>
          </div>
        </article>
      {editName ? (
          <form
            className="profile-name-form"
            aria-label="Изменить имя"
            onSubmit={event => {
              event.preventDefault();
              void save();
            }}
          >
            <label>
              <input
                autoFocus
                aria-label="Имя"
                placeholder="Ваше имя"
                maxLength={80}
                value={name}
                onChange={event => {
                  setName(event.target.value);
                  setServerError('');
                }}
              />
            </label>
            {serverError && <p className="error">{serverError}</p>}
            <button
              type="submit"
              className="button button-primary"
              disabled={!name.trim() || pending}
            >
              {pending ? 'Сохраняем…' : 'Сохранить'}
            </button>
          </form>
      ) : (
        <button type="button" className="profile-edit-name" onClick={() => {
          setName(user.displayName);
          setPending(false);
          const next = new URLSearchParams(searchParams);
          next.set('overlay', 'edit-profile-name');
          setSearchParams(next, { state: location.state });
        }}><Pencil size={18} />Изменить имя</button>
      )}
      </section>
    </Dialog>
  );
}

export function SessionsPage() {
  const { apiGet, apiMutation } = useAccountApi();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const sessions = useQuery({
    queryKey: ['sessions'],
    queryFn: () =>
      apiGet<Envelope<Session[]>>('/me/sessions').then(
        result => result.data,
      ),
  });
  const selectedSessionId = searchParams.get('sessionId');
  const selectedSession = sessions.data?.find(
    item => item.id === selectedSessionId,
  );
  const showActions =
    searchParams.get('overlay') === 'session-actions' &&
    selectedSession !== undefined;
  const closeActions = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('overlay');
    next.delete('sessionId');
    setSearchParams(next, { replace: true, state: location.state });
  };
  const logout = async () => {
    await apiMutation('/auth/web/logout', { scope: 'web-auth:logout' });
    queryClient.clear();
    window.location.assign('/login');
  };
  const revoke = async (session: Session) => {
    await apiMutation(`/me/sessions/${session.id}`, {
      method: 'DELETE',
      scope: `session:revoke:${session.id}`,
    });
    if (session.current) {
      queryClient.clear();
      window.location.assign('/login');
      return;
    }
    closeActions();
    await sessions.refetch();
  };
  if (sessions.isPending) return <Loading />;
  if (sessions.isError) return <ReadError />;
  return (
    <Dialog
      title="Сессии и устройства"
      onClose={() => navigate('/account', { replace: true })}
    >
      <section className="sessions-page">
        <p>
          Здесь показаны устройства, на которых выполнен вход в Казну.
        </p>
        <div className="session-card-list">
          {sessions.data?.map(session => (
            <button
              type="button"
              className="session-card"
              key={session.id}
              onClick={() => {
                const next = new URLSearchParams(searchParams);
                next.set('overlay', 'session-actions');
                next.set('sessionId', session.id);
                setSearchParams(next, { state: location.state });
              }}
            >
              <span><Smartphone size={21} /></span>
              <div>
                <strong>
                  {platformName(session.platform)}
                  {session.current ? ' · это устройство' : ''}
                </strong>
                <small>
                  Активность{' '}
                  {new Date(session.lastSeenAt).toLocaleString('ru-RU')}
                </small>
                <small>{session.appVersion}</small>
              </div>
              <ChevronRight size={20} />
            </button>
          ))}
        </div>
        <button
          type="button"
          className="sessions-logout"
          onClick={() => void logout()}
        >
          Выйти из Казны Web
        </button>
      </section>
      {showActions && selectedSession && (
        <Dialog
          variant="sheet"
          title={platformName(selectedSession.platform)}
          onClose={closeActions}
        >
          <div className="mobile-choice-list session-action-sheet">
            <button
              type="button"
              className="destructive"
              onClick={() => void revoke(selectedSession)}
            >
              <span><Trash2 size={21} /></span>
              <b>
                {selectedSession.current
                  ? 'Выйти на этом устройстве'
                  : 'Отозвать сессию'}
              </b>
              <ChevronRight size={20} />
            </button>
          </div>
        </Dialog>
      )}
    </Dialog>
  );
}

export function WorkspacesPage() {
  const { workspace, workspaces, setWorkspaceId } = useAppData();
  const navigate = useNavigate();

  return (
    <Dialog title="Пространства" onClose={() => navigate('/account')}>
      <div className="workspace-mobile-page">
        <p className="workspace-hint">
          Здесь настраиваются пространства. Переключить активное пространство можно кнопкой слоёв на главной панели.
        </p>
        <section className="workspace-list-card">
          {workspaces.map((item, index) => {
            const selected = item.id === workspace?.id;
            return (
              <article
                className={`workspace-list-row ${selected ? 'selected' : ''}`}
                key={item.id}
              >
                <button
                  type="button"
                  className="workspace-row-main"
                  aria-label={`Управление пространством ${item.name}`}
                  onClick={() =>
                    navigate(`/account/workspaces/${item.id}`)
                  }
                >
                  <span className="workspace-cloud">
                    <Cloud size={20} />
                  </span>
                  <span className="workspace-row-copy">
                    <strong>{item.name}</strong>
                    <small>
                      Общее пространство ·{' '}
                      {workspaceRoleLabel(item.currentMembership.role).toLowerCase()}
                    </small>
                  </span>
                  {selected ? (
                    <span className="workspace-active-badge"><Check size={17} />Активно</span>
                  ) : (
                    <ChevronRight size={18} />
                  )}
                </button>
                {!selected && (
                  <button
                    type="button"
                    className="workspace-open"
                    onClick={() => {
                      setWorkspaceId(item.id);
                      navigate('/dashboard');
                    }}
                  >
                    Открыть
                  </button>
                )}
                {index < workspaces.length - 1 && (
                  <span className="workspace-row-separator" />
                )}
              </article>
            );
          })}
          {workspaces.length === 0 && (
            <p className="workspace-empty">Пространств пока нет</p>
          )}
        </section>
        <button
          type="button"
          className="workspace-large-action primary-soft"
          onClick={() => navigate('/account/workspaces/new')}
        >
          <Plus size={22} />
          Новое пространство
        </button>
        <button
          type="button"
          className="workspace-large-action outlined"
          onClick={() => navigate('/account/join')}
        >
          <Users size={21} />
          Вступить по приглашению
        </button>
      </div>
    </Dialog>
  );
}

export function CreateWorkspacePage() {
  const { apiMutation } = useAccountApi();
  const { workspace, workspaces, setWorkspaceId } = useAppData();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState('');

  const create = async () => {
    if (!name.trim() || pending) return;
    setPending(true);
    setError('');
    try {
      const result = await apiMutation<Envelope<Workspace>>('/workspaces', {
        scope: 'workspace:create',
        body: {
          name: name.trim(),
          timezone:
            Intl.DateTimeFormat().resolvedOptions().timeZone || 'Europe/Moscow',
        },
      });
      await queryClient.invalidateQueries({ queryKey: ['workspaces'] });
      setWorkspaceId(result.data.id);
      navigate(`/account/workspaces/${result.data.id}`, { replace: true });
    } catch (nextError) {
      setError(errorMessage(nextError, 'Не удалось создать пространство'));
    } finally {
      setPending(false);
    }
  };

  return (
    <Dialog title="Пространства" onClose={() => navigate('/account/workspaces')}>
      <div className="workspace-mobile-page">
        <p className="workspace-hint">
          У каждого пространства свои категории, бюджеты и операции.
        </p>
        <section className="workspace-list-card compact">
          {workspaces.map(item => (
            <article className="workspace-list-row" key={item.id}>
              <div className="workspace-row-main">
                <span className="workspace-cloud">
                  <Cloud size={23} strokeWidth={2.5} />
                </span>
                <span className="workspace-row-copy">
                  <strong>{item.name}</strong>
                  <small>
                    Общее пространство ·{' '}
                    {workspaceRoleLabel(item.currentMembership.role).toLowerCase()}
                  </small>
                </span>
                {item.id === workspace?.id && <Check size={23} />}
              </div>
            </article>
          ))}
        </section>
        <section className="workspace-create-card">
          <input
            autoFocus
            aria-label="Название пространства"
            maxLength={120}
            placeholder="Название пространства"
            value={name}
            onChange={event => setName(event.target.value)}
            onKeyDown={event => {
              if (event.key === 'Enter') {
                event.preventDefault();
                void create();
              }
            }}
          />
          {error && <p className="error">{error}</p>}
          <button
            type="button"
            className="workspace-primary"
            disabled={!name.trim() || pending}
            onClick={() => void create()}
          >
            {pending ? 'Создаём…' : 'Создать'}
          </button>
        </section>
      </div>
    </Dialog>
  );
}

export function WorkspaceDetailsPage() {
  const { apiMutation, assertCurrent } = useAccountApi();
  const { workspace: activeWorkspace, workspaces, setWorkspaceId } = useAppData();
  const { workspaceId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const workspace = workspaces.find(item => item.id === workspaceId);
  const [name, setName] = useState<string>();
  const [pending, setPending] = useState(false);
  const [error, setError] = useState('');

  if (workspace === undefined) {
    return (
      <Dialog title="Управление" onClose={() => navigate('/account/workspaces')}>
        <section className="empty-state">
          <h2>Пространство не найдено</h2>
        </section>
      </Dialog>
    );
  }

  const role = workspace.currentMembership.role;
  const canManage = role === 'OWNER' || role === 'ADMIN';
  const save = async () => {
    const nextName = name?.trim() ?? workspace.name;
    if (!canManage || !nextName || nextName === workspace.name || pending)
      return;
    setPending(true);
    setError('');
    try {
      await apiMutation(`/workspaces/${workspace.id}`, {
        method: 'PATCH',
        scope: `workspace:update:${workspace.id}`,
        version: workspace.version,
        body: { name: nextName },
      });
      await queryClient.invalidateQueries({ queryKey: ['workspaces'] });
    } catch (nextError) {
      await queryClient.invalidateQueries({ queryKey: ['workspaces'] });
      setError(
        nextError instanceof ApiError && nextError.status === 409
          ? 'Пространство изменилось на другом устройстве. Данные обновлены.'
          : errorMessage(nextError, 'Не удалось сохранить название'),
      );
    } finally {
      setPending(false);
    }
  };
  const remove = async () => {
    if (
      role !== 'OWNER' ||
      !window.confirm(
        `Удалить пространство «${workspace.name}»? Восстановить его будет нельзя.`,
      )
    )
      return;
    setPending(true);
    setError('');
    try {
      await apiMutation(`/workspaces/${workspace.id}`, {
        method: 'DELETE',
        scope: `workspace:delete:${workspace.id}`,
        version: workspace.version,
      });
      assertCurrent();
      localStorage.removeItem('kazna:active-workspace');
      await queryClient.invalidateQueries({ queryKey: ['workspaces'] });
      navigate('/account/workspaces', { replace: true });
    } catch (nextError) {
      await queryClient.invalidateQueries({ queryKey: ['workspaces'] });
      setError(
        nextError instanceof ApiError && nextError.status === 409
          ? 'Пространство изменилось на другом устройстве. Данные обновлены.'
          : errorMessage(nextError, 'Не удалось удалить пространство'),
      );
    } finally {
      setPending(false);
    }
  };

  return (
    <Dialog title="Управление" onClose={() => navigate('/account/workspaces')}>
      <div className="workspace-mobile-page workspace-manage-page">
        <section className="workspace-manage-card">
          <label>
            <span>Название</span>
            <input
              aria-label="Название пространства"
              disabled={!canManage}
              maxLength={120}
              value={name ?? workspace.name}
              onChange={event => setName(event.target.value)}
            />
          </label>
          {error && <p className="error">{error}</p>}
          <button
            type="button"
            className="workspace-primary"
            disabled={
              !canManage ||
              !(name ?? workspace.name).trim() ||
              (name ?? workspace.name).trim() === workspace.name ||
              pending
            }
            onClick={() => void save()}
          >
            {pending ? 'Сохраняем…' : 'Сохранить'}
          </button>
        </section>
        {activeWorkspace?.id !== workspace.id && (
          <button
            type="button"
            className="workspace-settings-row"
            onClick={() => {
              setWorkspaceId(workspace.id);
              navigate('/dashboard');
            }}
          >
            <span className="workspace-settings-icon">
              <Cloud size={23} />
            </span>
            <span>
              <strong>Сделать активным</strong>
              <small>Открыть панель этого пространства</small>
            </span>
            <ChevronRight size={21} />
          </button>
        )}
        <button
          type="button"
          className="workspace-settings-row"
          onClick={() =>
            navigate(`/account/workspaces/${workspace.id}/members`)
          }
        >
          <span className="workspace-settings-icon">
            <Users size={23} />
          </span>
          <span>
            <strong>Участники</strong>
            <small>Роли, приглашения и доступ</small>
          </span>
          <ChevronRight size={21} />
        </button>
        <section className="workspace-role-card">
          <span>Ваша роль</span>
          <strong>{workspaceRoleLabel(role)}</strong>
        </section>
        {role === 'OWNER' && (
          <button
            type="button"
            className="workspace-delete"
            disabled={pending}
            onClick={() => void remove()}
          >
            <Trash2 size={21} />
            Удалить навсегда
          </button>
        )}
      </div>
    </Dialog>
  );
}

export function WorkspaceMembersPage() {
  const { apiGet, apiMutation } = useAccountApi();
  const { user, workspace: activeWorkspace, workspaces } = useAppData();
  const { workspaceId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const workspace =
    workspaces.find(item => item.id === workspaceId) ?? activeWorkspace;
  const members = useQuery({
    queryKey: ['members', workspace?.id],
    enabled: workspace !== undefined,
    queryFn: () =>
      apiGet<Envelope<Member[]>>(
        `/workspaces/${workspace!.id}/members`,
      ).then(result => result.data),
  });
  const [inviteRole, setInviteRole] = useState<'ADMIN' | 'MEMBER'>('MEMBER');
  const [inviteText, setInviteText] = useState('');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState('');
  const selectedMember = members.data?.find(
    item => item.id === searchParams.get('memberId'),
  );
  const overlay = searchParams.get('overlay');
  const role = workspace?.currentMembership.role;
  const canManage = role === 'OWNER' || role === 'ADMIN';

  if (workspace === undefined) {
    return (
      <Dialog title="Участники" onClose={() => navigate('/account')}>
        <section className="empty-state">
          <h2>Пространство не найдено</h2>
        </section>
      </Dialog>
    );
  }

  const closeOverlay = () => navigate(-1);
  const openMember = (member: Member) => {
    if (!canManage || member.userId === user.id || member.role === 'OWNER')
      return;
    const next = new URLSearchParams(searchParams);
    next.set('overlay', 'member');
    next.set('memberId', member.id);
    setSearchParams(next);
  };
  const createInvite = async () => {
    if (!canManage || pending) return;
    setPending(true);
    setError('');
    try {
      const result = await apiMutation<Envelope<{ token: string }>>(
        `/workspaces/${workspace.id}/invites`,
        {
          scope: `workspace:invite:${workspace.id}:${inviteRole}`,
          body: { role: inviteRole, expiresInSeconds: 86400 },
        },
      );
      const link = `${window.location.origin}/invite/${result.data.token}`;
      setInviteText(
        `Присоединяйтесь к пространству «${workspace.name}» в Казне:\n${link}\n\nКод: ${result.data.token}`,
      );
      const next = new URLSearchParams(searchParams);
      next.set('overlay', 'invite');
      next.delete('memberId');
      setSearchParams(next);
    } catch (nextError) {
      setError(errorMessage(nextError, 'Не удалось создать приглашение'));
    } finally {
      setPending(false);
    }
  };
  const changeMember = async (
    member: Member,
    action: 'role' | 'remove' | 'owner',
  ) => {
    if (!canManage || pending || member.role === 'OWNER') return;
    if (
      action === 'owner' &&
      !window.confirm(`Передать владение пользователю ${member.displayName}?`)
    )
      return;
    if (
      action === 'remove' &&
      !window.confirm(`Удалить ${member.displayName} из пространства?`)
    )
      return;
    setPending(true);
    setError('');
    try {
      if (action === 'owner') {
        if (role !== 'OWNER') return;
        await apiMutation(
          `/workspaces/${workspace.id}/ownership-transfer`,
          {
            scope: `ownership:${workspace.id}:${member.id}`,
            body: { targetMemberId: member.id },
          },
        );
      } else if (action === 'remove') {
        await apiMutation(
          `/workspaces/${workspace.id}/members/${member.id}`,
          {
            method: 'DELETE',
            scope: `member:remove:${workspace.id}:${member.id}`,
            version: member.version,
          },
        );
      } else {
        if (role !== 'OWNER') return;
        await apiMutation(
          `/workspaces/${workspace.id}/members/${member.id}`,
          {
            method: 'PATCH',
            scope: `member:role:${workspace.id}:${member.id}`,
            version: member.version,
            body: { role: member.role === 'ADMIN' ? 'MEMBER' : 'ADMIN' },
          },
        );
      }
      closeOverlay();
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ['members', workspace.id],
        }),
        queryClient.invalidateQueries({ queryKey: ['workspaces'] }),
      ]);
    } catch (nextError) {
      await members.refetch();
      setError(
        nextError instanceof ApiError && nextError.status === 409
          ? 'Участник изменён на другом устройстве. Данные обновлены.'
          : errorMessage(nextError, 'Не удалось изменить участника'),
      );
    } finally {
      setPending(false);
    }
  };

  return (
    <Dialog
      title="Участники"
      onClose={() =>
        navigate(
          workspaceId
            ? `/account/workspaces/${workspace.id}`
            : '/account',
        )
      }
    >
      <div className="workspace-mobile-page members-mobile-page">
        <p className="workspace-hint">
          Участники имеют доступ к общему семейному бюджету.
        </p>
        <section className="members-card">
          {members.isPending && <p className="workspace-empty">Загружаем…</p>}
          {members.data
            ?.filter(member => member.status !== 'LEFT')
            .map((member, index, visibleMembers) => (
              <button
                type="button"
                className="member-mobile-row"
                disabled={
                  !canManage ||
                  member.userId === user.id ||
                  member.role === 'OWNER'
                }
                key={member.id}
                onClick={() => openMember(member)}
              >
                <span
                  className="member-mobile-avatar"
                  style={{ background: member.color }}
                >
                  {member.initials}
                </span>
                <span className="member-mobile-copy">
                  <strong>{member.displayName}</strong>
                  <small>
                    {workspaceRoleLabel(member.role)} ·{' '}
                    {memberStatusLabel(member.status)}
                  </small>
                </span>
                {member.role === 'OWNER' ? (
                  <Crown size={23} />
                ) : member.role === 'ADMIN' ? (
                  <ShieldCheck size={23} />
                ) : (
                  <UserRound size={23} />
                )}
                {index < visibleMembers.length - 1 && (
                  <span className="member-mobile-separator" />
                )}
              </button>
            ))}
        </section>
        {canManage && (
          <section className="invite-mobile-card">
            <h3>Новое приглашение</h3>
            <div className="invite-role-segment">
              <button
                type="button"
                className={inviteRole === 'MEMBER' ? 'active' : ''}
                onClick={() => setInviteRole('MEMBER')}
              >
                Участник
              </button>
              <button
                type="button"
                className={inviteRole === 'ADMIN' ? 'active' : ''}
                disabled={role !== 'OWNER'}
                onClick={() => setInviteRole('ADMIN')}
              >
                Администратор
              </button>
            </div>
            <button
              type="button"
              className="workspace-primary invite-share"
              disabled={pending}
              onClick={() => void createInvite()}
            >
              <UserRoundPlus size={22} />
              {pending ? 'Создаём…' : 'Поделиться ссылкой и кодом'}
            </button>
          </section>
        )}
        {error && <p className="error">{error}</p>}
      </div>
      {overlay === 'invite' && (
        <Dialog variant="sheet" title="Приглашение готово" onClose={closeOverlay}>
          <div className="invite-result">
            <p>Отправьте ссылку или одноразовый код будущему участнику.</p>
            <textarea
              aria-label="Текст приглашения"
              readOnly
              value={inviteText}
              onFocus={event => event.currentTarget.select()}
            />
            <button
              type="button"
              className="workspace-primary"
              onClick={() => void navigator.clipboard?.writeText(inviteText)}
            >
              <Copy size={20} />
              Скопировать
            </button>
          </div>
        </Dialog>
      )}
      {overlay === 'member' && selectedMember && (
        <Dialog
          variant="sheet"
          title={selectedMember.displayName}
          onClose={closeOverlay}
        >
          <div className="mobile-choice-list member-action-sheet">
            {role === 'OWNER' && (
              <>
                <button
                  type="button"
                  onClick={() => void changeMember(selectedMember, 'role')}
                >
                  <span><ShieldCheck size={21} /></span>
                  <b>
                    {selectedMember.role === 'ADMIN'
                      ? 'Сделать участником'
                      : 'Сделать администратором'}
                  </b>
                  <ChevronRight size={20} />
                </button>
                <button
                  type="button"
                  onClick={() => void changeMember(selectedMember, 'owner')}
                >
                  <span><Crown size={21} /></span>
                  <b>Передать владение</b>
                  <ChevronRight size={20} />
                </button>
              </>
            )}
            <button
              type="button"
              className="destructive"
              onClick={() => void changeMember(selectedMember, 'remove')}
            >
              <span><Trash2 size={21} /></span>
              <b>Удалить участника</b>
              <ChevronRight size={20} />
            </button>
          </div>
        </Dialog>
      )}
    </Dialog>
  );
}

export function JoinWorkspacePage() {
  const { apiMutation, apiGet } = useAccountApi();
  const { token: routeToken } = useParams();
  const { workspaces, setWorkspaceId } = useAppData();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [code, setCode] = useState(routeToken ?? '');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState('');

  const accept = async () => {
    const token = code.trim().replace(/^.*\/invite\//u, '').split(/[?#]/u)[0];
    if (!token || pending) return;
    setPending(true);
    setError('');
    try {
      const previousIds = new Set(workspaces.map(item => item.id));
      await apiMutation(`/invites/${encodeURIComponent(token)}/accept`, {
        scope: `invite:accept:${token}`,
      });
      await queryClient.invalidateQueries({ queryKey: ['workspaces'] });
      const fresh = await queryClient.fetchQuery({
        queryKey: ['workspaces'],
        queryFn: () =>
          apiGet<Envelope<Workspace[]>>('/workspaces').then(
            result => result.data,
          ),
      });
      const joined =
        fresh.find(
          item =>
            !previousIds.has(item.id) &&
            item.currentMembership.status === 'ACTIVE',
        ) ??
        fresh.find(item => item.currentMembership.status === 'ACTIVE');
      if (joined !== undefined) setWorkspaceId(joined.id);
      navigate('/account/workspaces', { replace: true });
    } catch (nextError) {
      setError(errorMessage(nextError, 'Не удалось принять приглашение'));
    } finally {
      setPending(false);
    }
  };

  return (
    <Dialog
      title="Вступить"
      onClose={() =>
        navigate(routeToken ? '/dashboard' : '/account/workspaces')
      }
    >
      <div className="workspace-mobile-page join-workspace-page">
        <p className="workspace-hint">
          Введите одноразовый код приглашения. После подтверждения пространство
          появится в общем списке.
        </p>
        <input
          autoFocus={!routeToken}
          aria-label="Код приглашения"
          autoCapitalize="none"
          placeholder="Код приглашения"
          value={code}
          onChange={event => setCode(event.target.value)}
        />
        <section className="join-account-card">
          <span className="member-mobile-avatar">
            <UserRound size={24} />
          </span>
          <span>
            <strong>Текущий Яндекс ID</strong>
            <small>Приглашение будет принято этим аккаунтом</small>
          </span>
          <Check size={22} />
        </section>
        {error && <p className="error">{error}</p>}
        <button
          type="button"
          className="workspace-primary join-submit"
          disabled={!code.trim() || pending}
          onClick={() => void accept()}
        >
          {pending ? 'Принимаем…' : 'Принять приглашение'}
        </button>
      </div>
    </Dialog>
  );
}

function workspaceRoleLabel(role: Member['role']): string {
  if (role === 'OWNER') return 'Владелец';
  if (role === 'ADMIN') return 'Администратор';
  return 'Участник';
}

function memberStatusLabel(status: Member['status']): string {
  if (status === 'INVITED') return 'приглашён';
  if (status === 'SUSPENDED') return 'доступ приостановлен';
  if (status === 'LEFT') return 'вышел';
  return 'активен';
}

function errorMessage(error: unknown, fallback: string): string {
  return error instanceof Error && error.message ? error.message : fallback;
}

export function AccountPage() {
  const { apiGet, apiMutation, assertCurrent } = useAccountApi();
  const { user, workspace, workspaces, setWorkspaceId, subscriptionEnabled } = useAppData();
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const section = location.pathname.replace(/^\/account\/?/u, '');
  const role = workspace?.currentMembership.role;
  const canManage = role === undefined ? false : canManageFinance(role);
  const sessions = useQuery({
    queryKey: ['sessions'],
    queryFn: () => apiGet<Envelope<Session[]>>('/me/sessions').then(result => result.data),
  });
  const accounts = useQuery({
    queryKey: ['finance', workspace?.id, 'accounts'],
    enabled: workspace !== undefined,
    queryFn: () =>
      apiGet<Envelope<Account[]>>(
        `/workspaces/${workspace!.id}/accounts?includeArchived=true`,
      ).then(result => result.data),
  });
  const categories = useQuery({
    queryKey: ['finance', workspace?.id, 'categories'],
    enabled: workspace !== undefined,
    queryFn: () =>
      apiGet<Envelope<Category[]>>(
        `/workspaces/${workspace!.id}/categories?includeArchived=true`,
      ).then(result => result.data),
  });
  const currentMonth = todayKey().slice(0, 7);
  const categoryLimits = useQuery({
    queryKey: ['finance', workspace?.id, 'category-limits', currentMonth],
    enabled: workspace !== undefined,
    queryFn: () =>
      apiGet<Envelope<CategoryLimit[]>>(
        `/workspaces/${workspace!.id}/category-limits?fromMonth=${currentMonth}&toMonth=${currentMonth}`,
      ).then(result => result.data),
  });
  const members = useQuery({
    queryKey: ['members', workspace?.id],
    enabled: workspace !== undefined,
    queryFn: () =>
      apiGet<Envelope<Member[]>>(`/workspaces/${workspace!.id}/members`).then(result => result.data),
  });
  const [profileName, setProfileName] = useState(user.displayName);
  const [workspaceName, setWorkspaceName] = useState(workspace?.name ?? '');
  const [newWorkspace, setNewWorkspace] = useState('');
  const [limitCategory, setLimitCategory] = useState('');
  const [limitAmount, setLimitAmount] = useState('');
  const [invite, setInvite] = useState<string>();
  const [inviteCode, setInviteCode] = useState('');
  const [actionError, setActionError] = useState('');

  const refresh = async () => {
    await queryClient.invalidateQueries();
  };
  const updateProfile = async () => {
    setActionError('');
    try {
      await apiMutation('/me', {
        method: 'PATCH',
        scope: 'me:update',
        version: user.version,
        body: { displayName: profileName },
      });
      await refresh();
    } catch (error) {
      await queryClient.invalidateQueries({ queryKey: ['me'] });
      setActionError(
        error instanceof ApiError && error.status === 409
          ? 'Профиль изменился на другом устройстве. Данные обновлены.'
          : error instanceof Error ? error.message : 'Не удалось сохранить профиль',
      );
    }
  };
  const createWorkspace = async () => {
    const result = await apiMutation<Envelope<Workspace>>('/workspaces', {
      scope: 'workspace:create',
      body: { name: newWorkspace, timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'Europe/Moscow' },
    });
    setWorkspaceId(result.data.id);
    setNewWorkspace('');
    await refresh();
  };
  const renameWorkspace = async () => {
    await apiMutation(`/workspaces/${workspace!.id}`, {
      method: 'PATCH',
      scope: `workspace:update:${workspace!.id}`,
      version: workspace!.version,
      body: { name: workspaceName },
    });
    await refresh();
  };
  const createInvite = async () => {
    const result = await apiMutation<Envelope<{ token: string }>>(
      `/workspaces/${workspace!.id}/invites`,
      { scope: `workspace:invite:${workspace!.id}`, body: { role: 'MEMBER', expiresInSeconds: 86400 } },
    );
    setInvite(
      `Присоединяйтесь к пространству «${workspace!.name}» в Казне:\n` +
      `${window.location.origin}/invite/${result.data.token}\n\nКод: ${result.data.token}`,
    );
  };
  const acceptInvite = async () => {
    const token = inviteCode.trim().replace(/^.*\/invite\//u, '');
    if (!token) return;
    await apiMutation(`/invites/${encodeURIComponent(token)}/accept`, {
      scope: `invite:accept:${token}`,
    });
    setInviteCode('');
    await refresh();
  };
  const setLimit = async () => {
    const categoryId = limitCategory || categories.data?.find(item => item.type === 'EXPENSE' && !item.system && !item.parentId)?.id;
    if (!categoryId) return;
    const existing = categoryLimits.data?.find(
      item => item.categoryId === categoryId && item.month === currentMonth,
    );
    await apiMutation(
      `/workspaces/${workspace!.id}/category-limits/${todayKey().slice(0, 7)}/${categoryId}`,
      {
        method: 'PUT',
        scope: `limit:set:${workspace!.id}:${categoryId}:${todayKey().slice(0, 7)}`,
        body: { limitMinor: rublesToMinor(limitAmount) },
        ...(existing === undefined
          ? { createOnly: true as const }
          : { version: existing.version }),
      },
    );
    setLimitAmount('');
    await refresh();
  };
  const deleteWorkspace = async () => {
    if (!window.confirm(`Удалить пространство «${workspace!.name}»? Это действие использует существующую серверную семантику удаления.`)) return;
    await apiMutation(`/workspaces/${workspace!.id}`, {
      method: 'DELETE',
      scope: `workspace:delete:${workspace!.id}`,
      version: workspace!.version,
    });
    assertCurrent();
    localStorage.removeItem('kazna:active-workspace');
    await refresh();
  };
  const toggleArchive = async (kind: 'accounts' | 'categories', item: Account | Category) => {
    const action = item.archivedAt ? 'restore' : 'archive';
    await apiMutation(`/workspaces/${workspace!.id}/${kind}/${item.id}/${action}`, {
      scope: `${kind}:${action}:${item.id}`,
      version: item.version,
    });
    await refresh();
  };
  const revokeSession = async (session: Session) => {
    await apiMutation(`/me/sessions/${session.id}`, {
      method: 'DELETE',
      scope: `session:revoke:${session.id}`,
    });
    if (session.current) window.location.assign('/login');
    else await sessions.refetch();
  };
  const logout = async () => {
    await apiMutation('/auth/web/logout', { scope: 'web-auth:logout' });
    queryClient.clear();
    window.location.assign('/login');
  };
  if (section === '') {
    const archiveCount =
      (accounts.data?.filter(item => item.archivedAt).length ?? 0) +
      (categories.data?.filter(
        item => !item.system && !item.parentId && item.archivedAt,
      ).length ?? 0);
    const categoryCount =
      categories.data?.filter(
        item =>
          item.type === 'EXPENSE' &&
          !item.system &&
          !item.parentId &&
          !item.archivedAt,
      ).length ?? 0;
    const roleLabel =
      role === 'OWNER'
        ? 'Владелец'
        : role === 'ADMIN'
          ? 'Администратор'
          : 'Участник';
    return (
      <section className="settings-home">
        <Link className="profile-card" to="/account/profile">
          <span className="avatar profile-avatar">{user.displayName.slice(0, 1).toUpperCase()}</span>
          <span>
            <strong>{user.displayName}</strong>
            <small>
              {roleLabel}
              {workspace ? ` пространства «${workspace.name}»` : ''}
            </small>
          </span>
          <ChevronRight size={22} />
        </Link>
        <SettingsGroup title="Семья и бюджет">
          <SettingsLink
            to="/account/workspaces"
            icon={Layers3}
            label="Пространства"
            subtitle="Раздельные категории и расходы"
            color="#149BFF"
          />
          <SettingsLink
            to="/account/budgets"
            icon={WalletCards}
            label="Счета и бюджеты"
            subtitle={`${accounts.data?.filter(item => !item.archivedAt).length ?? 0} бюджетов`}
            color="#20A779"
          />
          <SettingsLink
            to="/limits"
            icon={CircleDollarSign}
            label="Категории и лимиты"
            subtitle={`${categoryCount} категорий`}
            color="#6C63E8"
          />
          <SettingsLink
            to="/archive"
            icon={Archive}
            label="Архив"
            subtitle={`${archiveCount} объектов`}
            color="#E79A39"
          />
          <SettingsLink
            to="/account/participants"
            icon={Users}
            label="Участники"
            subtitle={`${members.data?.filter(item => item.status === 'ACTIVE').length ?? 0} участников`}
            color="#E85C9D"
          />
        </SettingsGroup>
        <SettingsGroup title="Аккаунт и синхронизация">
          {subscriptionEnabled && <SettingsLink to="/account/subscription" icon={CircleDollarSign} label="Подписка" subtitle="Тариф и срок доступа" color="#149BFF" />}
          <SettingsLink to="/account/sign-in-methods" icon={ShieldCheck} label="Способы входа" subtitle="Яндекс ID и почта" color="#149BFF" />
          <SettingsLink
            to="/account/sessions"
            icon={Smartphone}
            label="Сессии и устройства"
            subtitle="Аккаунт и активные входы"
            color="#149BFF"
          />
        </SettingsGroup>
      </section>
    );
  }
  return (
    <section className="settings-grid">
      <header className="subpage-header">
        <Link to="/account" aria-label="Назад к настройкам"><ChevronLeft size={25} /></Link>
        <h1>{settingsSectionTitle(section)}</h1>
        <span />
      </header>
      <article className="card" hidden={section !== 'profile'}>
        <header className="card-header"><div><p className="eyebrow">Яндекс ID</p><h2>Профиль</h2></div></header>
        <label className="field"><span>Имя</span><input value={profileName} onChange={event => setProfileName(event.target.value)} /></label>
        <p className="muted">Казна ID: <code>{user.id}</code></p>
        {actionError && <p className="error">{actionError}</p>}
        <button className="button button-primary" onClick={() => void updateProfile()}>Сохранить имя</button>
      </article>
      <article className="card span-two" hidden={section !== 'sessions'}>
        <header className="card-header"><div><p className="eyebrow">Доступ</p><h2>Сессии и устройства</h2></div></header>
        <div className="stack-list">
          {sessions.data?.map(session => (
            <div className="row-between session-row" key={session.id}>
              <div><strong>{platformName(session.platform)} {session.current && '· это устройство'}</strong><small>{new Date(session.lastSeenAt).toLocaleString('ru-RU')}</small></div>
              <button className="button button-quiet" onClick={() => void revokeSession(session)}>{session.current ? 'Выйти' : 'Отозвать'}</button>
            </div>
          ))}
        </div>
        <button className="button button-secondary" onClick={() => void logout()}>Выйти из Казны Web</button>
      </article>
      <article className="card span-two" hidden={!['workspaces', 'participants'].includes(section)}>
        <header className="card-header"><div><p className="eyebrow">Серверные пространства</p><h2>Управление</h2></div></header>
        <div className="chip-row" hidden={section === 'participants'}>
          {workspaces.map(item => <button key={item.id} className={item.id === workspace?.id ? 'chip active' : 'chip'} onClick={() => setWorkspaceId(item.id)}>{item.name}</button>)}
        </div>
        <div className="inline-form" hidden={section === 'participants'}><input placeholder="Новое пространство" value={newWorkspace} onChange={event => setNewWorkspace(event.target.value)} /><button className="button button-secondary" disabled={!newWorkspace.trim()} onClick={() => void createWorkspace()}>Создать</button></div>
        {section !== 'participants' && workspace && canManage && (
          <>
            <div className="inline-form"><input value={workspaceName} onChange={event => setWorkspaceName(event.target.value)} /><button className="button button-secondary" onClick={() => void renameWorkspace()}>Переименовать</button></div>
            <div className="inline-form"><button className="button button-secondary" onClick={() => void createInvite()}><Users size={17}/> Создать приглашение</button>{invite && <textarea aria-label="Текст приглашения" readOnly value={invite} onFocus={event => event.currentTarget.select()} />}</div>
            {role === 'OWNER' && <button className="button button-quiet negative" onClick={() => void deleteWorkspace()}><Trash2 size={17}/> Удалить пространство</button>}
          </>
        )}
        {section === 'workspaces' && <div className="inline-form"><input aria-label="Код приглашения" placeholder="Код или ссылка приглашения" value={inviteCode} onChange={event => setInviteCode(event.target.value)} /><button className="button button-secondary" disabled={!inviteCode.trim()} onClick={() => void acceptInvite()}>Присоединиться</button></div>}
        <MemberManager members={members.data ?? []} canManage={canManage} isOwner={role === 'OWNER'} onChanged={refresh} />
      </article>
      <article className="card" hidden={section !== 'budgets'}>
        <header className="card-header"><div><p className="eyebrow">Финансовая структура</p><h2>Счета и бюджеты</h2></div></header>
        {canManage && (
          <button
            type="button"
            className="button button-secondary wide structure-create"
            onClick={() =>
              navigate('/budgets/new', {
                state: { returnTo: '/account/budgets' },
              })
            }
          >
            <Plus size={18} /> Новый бюджет
          </button>
        )}
        <div className="stack-list compact">{accounts.data?.map(item => <StructureRow key={item.id} name={item.name} archived={Boolean(item.archivedAt)} disabled={!canManage} onEdit={() => navigate(`/budgets/${item.id}/edit`, { state: { returnTo: '/account/budgets' } })} onToggle={() => void toggleArchive('accounts', item)} />)}</div>
      </article>
      <article className="card" hidden={!['categories', 'limits'].includes(section)}>
        <header className="card-header"><div><p className="eyebrow">Финансовая структура</p><h2>Категории</h2></div></header>
        {canManage && <>
          <div className="category-create-actions" hidden={section === 'limits'}>
            <button
              type="button"
              onClick={() =>
                navigate('/categories/new?kind=expense', {
                  state: { returnTo: '/account/categories' },
                })
              }
            >
              <Plus size={17} />
              Новый расход
            </button>
            <button
              type="button"
              onClick={() =>
                navigate('/categories/new?kind=income', {
                  state: { returnTo: '/account/categories' },
                })
              }
            >
              <Plus size={17} />
              Новый доход
            </button>
          </div>
          <div className="inline-form" hidden={section === 'categories'}><select value={limitCategory} onChange={event => setLimitCategory(event.target.value)}>{categories.data?.filter(item => item.type === 'EXPENSE' && !item.system && !item.parentId && !item.archivedAt).map(item => <option key={item.id} value={item.id}>{item.name}</option>)}</select><input inputMode="decimal" placeholder="Лимит на месяц, ₽" value={limitAmount} onChange={event => setLimitAmount(event.target.value)} /><button className="button button-secondary" disabled={!limitAmount} onClick={() => void setLimit()}>Задать лимит</button></div>
        </>}
        <div className="stack-list compact" hidden={section === 'limits'}>{categories.data?.filter(item => !item.system).map(item => <StructureRow key={item.id} name={`${item.parentId ? '↳ ' : ''}${item.name}`} archived={Boolean(item.archivedAt)} disabled={!canManage} onEdit={() => navigate(`/categories/${item.id}/edit`, { state: { returnTo: '/account/categories' } })} onToggle={() => void toggleArchive('categories', item)} />)}</div>
      </article>
      <article className="card" hidden={section !== 'archive'}>
        <header className="card-header"><div><p className="eyebrow">Восстановление</p><h2>Архив</h2></div></header>
        <div className="stack-list compact">
          {accounts.data?.filter(item => item.archivedAt).map(item => <StructureRow key={item.id} name={item.name} archived disabled={!canManage} onEdit={() => undefined} onToggle={() => void toggleArchive('accounts', item)} />)}
          {categories.data?.filter(item => !item.system && item.archivedAt).map(item => <StructureRow key={item.id} name={item.name} archived disabled={!canManage} onEdit={() => undefined} onToggle={() => void toggleArchive('categories', item)} />)}
          {!accounts.data?.some(item => item.archivedAt) && !categories.data?.some(item => item.archivedAt) && <p className="muted">В архиве пока ничего нет</p>}
        </div>
      </article>
    </section>
  );
}

export function StructureCreatePage({
  forcedKind,
}: {
  forcedKind?: 'budget';
}) {
  const { apiGet, apiMutation } = useAccountApi();
  const { workspace } = useAppData();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { categoryId, budgetId } = useParams();
  const [searchParams] = useSearchParams();
  const [archivingBudget, setArchivingBudget] = useState(false);
  const [pageError, setPageError] = useState('');
  const kind =
    forcedKind ??
    (searchParams.get('kind') === 'income' ? 'income' : 'expense');
  const requestedReturnPath = searchParams.get('returnPath');
  const returnTo =
    requestedReturnPath?.startsWith('/') === true
      ? requestedReturnPath
      : searchParams.get('returnTo') === 'dashboard'
        ? '/dashboard'
        : (location.state as { returnTo?: string } | null)?.returnTo ??
          (forcedKind === 'budget'
            ? '/account/budgets'
            : '/account/categories');
  const close = () => navigate(returnTo, { replace: true });
  const dashboardAnchor = todayKey();
  const categories = useQuery({
    queryKey: ['finance', workspace?.id, 'categories'],
    enabled: workspace !== undefined && categoryId !== undefined,
    queryFn: () =>
      apiGet<Envelope<Category[]>>(
        `/workspaces/${workspace!.id}/categories?includeArchived=true`,
      ).then(result => result.data),
  });
  const accounts = useQuery({
    queryKey: ['finance', workspace?.id, 'accounts'],
    enabled: workspace !== undefined && budgetId !== undefined,
    queryFn: () =>
      apiGet<Envelope<Account[]>>(
        `/workspaces/${workspace!.id}/accounts?includeArchived=true`,
      ).then(result => result.data),
  });
  const dashboard = useQuery({
    queryKey: ['dashboard', workspace?.id, 'MONTH', dashboardAnchor],
    enabled:
      workspace !== undefined &&
      (categoryId !== undefined || budgetId !== undefined),
    queryFn: () =>
      apiGet<Envelope<Dashboard>>(
        `/workspaces/${workspace!.id}/dashboard?period=MONTH&anchor=${dashboardAnchor}`,
      ).then(result => result.data),
  });
  const category = categories.data?.find(item => item.id === categoryId);
  const account = accounts.data?.find(item => item.id === budgetId);
  const initialAmountMinor = structureFormAmountMinor(
    category,
    account,
    dashboard.data,
  );
  if (
    (categoryId !== undefined &&
      (categories.isPending || dashboard.isPending)) ||
    (budgetId !== undefined && (accounts.isPending || dashboard.isPending))
  ) {
    return <Loading />;
  }
  if (
    (categoryId !== undefined && category === undefined) ||
    (budgetId !== undefined && account === undefined) ||
    ((categoryId !== undefined || budgetId !== undefined) &&
      initialAmountMinor === undefined)
  ) {
    return <ReadError />;
  }
  const resolvedKind =
    category === undefined
      ? kind
      : category.type === 'INCOME'
        ? 'income'
        : 'expense';
  const title =
    account !== undefined
      ? 'Редактировать бюджет'
      : category === undefined
        ? resolvedKind === 'budget'
          ? 'Новый бюджет'
          : 'Новая категория'
      : resolvedKind === 'income'
        ? 'Редактировать доход'
        : 'Редактировать расход';
  const archiveBudget = async () => {
    if (
      account === undefined ||
      !window.confirm(
        `Переместить «${account.name}» в архив? Баланс и все операции сохранятся в истории.`,
      )
    ) {
      return;
    }
    setArchivingBudget(true);
    setPageError('');
    try {
      await apiMutation(
        `/workspaces/${workspace!.id}/accounts/${account.id}/archive`,
        {
          scope: `accounts:archive:${account.id}`,
          version: account.version,
        },
      );
      await queryClient.invalidateQueries({
        predicate: query => query.queryKey.includes(workspace!.id),
      });
      close();
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 409) {
        await queryClient.invalidateQueries({
          predicate: query => query.queryKey.includes(workspace!.id),
        });
        setPageError(
          'Бюджет изменён на другом устройстве. Данные обновлены — откройте форму снова.',
        );
      } else {
        setPageError(
          caught instanceof Error
            ? caught.message
            : 'Не удалось переместить бюджет в архив',
        );
      }
      setArchivingBudget(false);
    }
  };
  return (
    <Dialog
      title={title}
      onClose={close}
      trailing={
        account === undefined ? undefined : (
          <button
            type="button"
            className="dialog-header-action"
            aria-label="Переместить бюджет в архив"
            disabled={archivingBudget}
            onClick={() => void archiveBudget()}
          >
            <Archive size={20} />
          </button>
        )
      }
    >
      <StructureCreateForm
        kind={resolvedKind}
        category={category}
        account={account}
        initialAmountMinor={initialAmountMinor}
        onDone={close}
      />
      {pageError && <p className="error">{pageError}</p>}
    </Dialog>
  );
}

function useOperationRouteState(): { returnTo: string } | null {
  const location = useLocation();
  const navigate = useNavigate();
  const state: unknown = location.state;
  const returnTo = typeof state === 'object' && state !== null && 'returnTo' in state
    ? safeActivityReturnTo(state.returnTo, '/activity')
    : undefined;
  useEffect(() => {
    if (typeof state !== 'object' || state === null || !('operation' in state)) return;
    // Old bundles persisted financial objects in history. Scrub only the entry
    // being visited; never use its object to seed the current account's cache.
    void navigate(`${location.pathname}${location.search}${location.hash}`, {
      replace: true,
      state: returnTo === undefined ? null : { returnTo },
    });
  }, [location.pathname, location.search, location.hash, navigate, returnTo, state]);
  return returnTo === undefined ? null : { returnTo };
}

export function OperationPage() {
  const { apiGet } = useAccountApi();
  const { workspace } = useAppData();
  const navigate = useNavigate();
  const routeState = useOperationRouteState();
  const { operationId } = useParams();
  const [searchParams] = useSearchParams();
  const snapshotQuery = useSnapshot();
  const operationQuery = useQuery({
    queryKey: ['finance', workspace?.id, 'operation', operationId],
    enabled: workspace !== undefined && operationId !== undefined,
    queryFn: () =>
      apiGet<Envelope<Operation>>(
        `/workspaces/${workspace!.id}/operations/${operationId!}`,
      ).then(result => result.data),
  });
  const requestedReturnPath = searchParams.get('returnPath');
  const returnTo = safeActivityReturnTo(requestedReturnPath ?? searchParams.get('returnTo') ?? routeState?.returnTo, searchParams.get('returnTo') === 'dashboard' ? '/dashboard' : '/activity');
  const close = () => navigate(returnTo, { replace: true });
  if (
    snapshotQuery.isPending ||
    (operationId !== undefined && operationQuery.isPending)
  ) {
    return <Loading />;
  }
  const snapshot = snapshotQuery.data?.snapshot;
  if (
    snapshot === undefined ||
    (operationId !== undefined && operationQuery.data === undefined)
  ) {
    return <ReadError />;
  }
  const requestedType = searchParams.get('type');
  const initialType: OperationDraft['type'] =
    requestedType === 'INCOME' || requestedType === 'TRANSFER'
      ? requestedType
      : 'EXPENSE';
  const initial: OperationDraft = {
    type: initialType,
    accountId: searchParams.get('accountId') ?? undefined,
    targetAccountId: searchParams.get('targetAccountId') ?? undefined,
    categoryId: searchParams.get('categoryId') ?? undefined,
  };
  const operation = operationQuery.data;
  if (operation?.debtId) {
    const movement = snapshotQuery.data?.snapshot.debtMovements.find(item => item.operationId === operation.id);
    return movement ? <Navigate to={`/debts/movements/${movement.id}/edit?${new URLSearchParams({ returnTo, detailReturnTo: safeActivityReturnTo(searchParams.get('detailReturnTo'), '/activity') })}`} replace state={{ returnTo }} /> : <Dialog title="Старое долговое движение" onClose={close}><p>Неизвестное старое движение нельзя изменять.</p></Dialog>;
  }
  const allowTransfer = searchParams.get('allowTransfer') === '1';
  const title =
    operation !== undefined
      ? 'Редактировать операцию'
      : initial.type === 'TRANSFER'
        ? 'Перевод'
        : allowTransfer
          ? 'Новая операция или перевод'
          : 'Новая операция';
  return (
    <Dialog title={title} onClose={close}>
      <OperationForm
        budgets={snapshot.budgets}
        categories={snapshot.categories}
        allowTransfer={allowTransfer}
        operation={operation}
        initial={initial}
        onDone={close}
      />
    </Dialog>
  );
}

export function CategoryDetailsPage() {
  const { apiGet } = useAccountApi();
  const { workspace } = useAppData();
  const navigate = useNavigate();
  const { categoryId } = useParams();
  const snapshotQuery = useSnapshot();
  const snapshot = snapshotQuery.data?.snapshot;
  const now = new Date();
  const periodFrom = new Date(now.getFullYear(), now.getMonth(), 1);
  const periodTo = new Date(now.getFullYear(), now.getMonth() + 1, 1);
  const operationsQuery = useQuery({
    queryKey: [
      'finance',
      workspace?.id,
      'category-operations',
      categoryId,
      periodFrom.toISOString(),
    ],
    enabled: workspace !== undefined,
    queryFn: () => {
      const params = new URLSearchParams({
        limit: '200',
        includeCancelled: 'false',
        from: periodFrom.toISOString(),
        to: periodTo.toISOString(),
      });
      return apiGet<Envelope<Operation[]>>(
        `/workspaces/${workspace!.id}/operations?${params}`,
      ).then(result => result.data);
    },
  });
  if (snapshotQuery.isPending || operationsQuery.isPending) return <Loading />;
  if (snapshot === undefined) return <ReadError />;
  const category = snapshot.categories.find(item => item.id === categoryId);
  if (category === undefined) return <ReadError />;
  const month = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  const categoryIds = new Set([
    category.id,
    ...snapshot.categories
      .filter(item => item.parentId === category.id)
      .map(item => item.id),
  ]);
  const transactions = (operationsQuery.data ?? [])
    .filter(
      item =>
        item.status === 'POSTED' &&
        typeof item.categoryId === 'string' &&
        categoryIds.has(item.categoryId),
    )
    .sort((left, right) => right.occurredAt.localeCompare(left.occurredAt));
  const spent = transactions
    .filter(item => item.type === 'EXPENSE')
    .reduce((sum, item) => sum + item.amountMinor, 0);
  const limit =
    snapshot.categoryLimits.find(
      item => item.categoryId === category.id && item.month === month,
    )?.limit ?? category.limit;
  const remaining = limit - spent;
  const exceeded = limit > 0 && remaining < 0;
  const progress = limit > 0 ? spent / limit : 0;
  const Icon = getFinanceIcon(category.icon, 'shopping-bag');
  const canManage = canManageFinance(
    workspace!.currentMembership.role,
  );
  const close = () => navigate('/dashboard', { replace: true });
  return (
    <Dialog
      title="Категория"
      onClose={close}
      trailing={
        canManage ? (
          <button
            type="button"
            className="dialog-header-action category-edit-action"
            aria-label="Редактировать категорию"
            onClick={() =>
              navigate(
                `/categories/${category.id}/edit?returnPath=${encodeURIComponent(`/categories/${category.id}`)}`,
                { state: { returnTo: `/categories/${category.id}` } },
              )
            }
          >
            <Pencil size={18} />
          </button>
        ) : undefined
      }
    >
      <section className="category-details">
        <article className="category-details-hero">
          <span
            className="category-details-icon"
            style={
              {
                '--category-color': category.color,
              } as CSSProperties
            }
          >
            {createElement(Icon, { size: 34 })}
          </span>
          <h1>{category.name}</h1>
          <p className="category-details-period">
            {new Intl.DateTimeFormat('ru-RU', {
              month: 'long',
              year: 'numeric',
            }).format(now)}
          </p>
          <div className="category-details-amounts">
            <div>
              <span>Потрачено</span>
              <strong>{formatMoney(spent)}</strong>
            </div>
            <div>
              <span>{exceeded ? 'Превышение' : limit > 0 ? 'Осталось' : 'Лимит'}</span>
              <strong className={exceeded ? 'negative' : 'positive'}>
                {limit > 0 ? formatMoney(Math.abs(remaining)) : 'Не задан'}
              </strong>
            </div>
          </div>
          <Progress value={progress} danger={exceeded} color={category.color} />
          <div className="category-details-progress-labels">
            <span className={exceeded ? 'negative' : ''}>
              {limit > 0 ? `${Math.round(progress * 100)}% лимита` : 'Лимит отключён'}
            </span>
            <span>
              {limit > 0
                ? `Лимит ${formatMoney(limit)}`
                : canManage
                  ? 'Нажмите ✎, чтобы задать'
                  : 'Лимит не задан'}
            </span>
          </div>
        </article>
        <h2>Операции</h2>
        <article className="category-transaction-card">
          {transactions.length === 0 ? (
            <p className="muted">В этой категории пока нет операций</p>
          ) : (
            transactions.map(transaction => {
              const member = snapshot.members.find(
                item =>
                  item.id === transaction.authorProjectionId ||
                  item.id === transaction.createdBy,
              );
              return (
                <button
                  type="button"
                  className="mobile-transaction-row"
                  key={transaction.id}
                  onClick={() =>
                    navigate(
                      `/transactions/${transaction.id}?returnTo=category&categoryId=${encodeURIComponent(category.id)}`,
                    )
                  }
                >
                  <span
                    className="mobile-transaction-icon"
                    style={
                      {
                        '--category-color': category.color,
                      } as CSSProperties
                    }
                  >
                    {createElement(Icon, { size: 20 })}
                  </span>
                  <span className="mobile-transaction-copy">
                    <strong>{category.name}</strong>
                    <small>
                      {formatCompactOperationDate(transaction.occurredAt)}
                      {member?.name ? ` · ${member.name}` : ''}
                      {transaction.note ? ` · ${transaction.note}` : ''}
                    </small>
                  </span>
                  <strong>
                    {transaction.type === 'INCOME' ? '+' : '−'}
                    {formatMoney(transaction.amountMinor)}
                  </strong>
                  <ChevronRight size={20} />
                </button>
              );
            })
          )}
        </article>
      </section>
    </Dialog>
  );
}

export function TransactionDetailsPage() {
  const { apiGet, apiMutation } = useAccountApi();
  const { workspace, user } = useAppData();
  const navigate = useNavigate();
  const routeState = useOperationRouteState();
  const queryClient = useQueryClient();
  const { operationId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const snapshotQuery = useSnapshot();
  const operationQuery = useQuery({
    queryKey: ['finance', workspace?.id, 'operation', operationId],
    enabled: workspace !== undefined && operationId !== undefined,
    queryFn: () =>
      apiGet<Envelope<Operation>>(
        `/workspaces/${workspace!.id}/operations/${operationId!}`,
      ).then(result => result.data),
  });
  const showActions = searchParams.get('overlay') === 'operation-actions';
  const closeActions = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('overlay');
    setSearchParams(next, { replace: true, state: routeState });
  };
  if (snapshotQuery.isPending || operationQuery.isPending) return <Loading />;
  const snapshot = snapshotQuery.data?.snapshot;
  const operation = operationQuery.data;
  if (snapshot === undefined || operation === undefined) return <ReadError />;
  const fallback = searchParams.get('returnTo') === 'category' && searchParams.get('categoryId') ? `/categories/${encodeURIComponent(searchParams.get('categoryId')!)}` : '/activity';
  const returnTo = safeActivityReturnTo(searchParams.get('returnTo') ?? searchParams.get('returnPath') ?? routeState?.returnTo, fallback);
  const movement = operation.debtId ? snapshot.debtMovements.find(item => item.operationId === operation.id) : undefined;
  if (movement) return <Navigate to={`/debts/movements/${movement.id}?${new URLSearchParams({ returnTo })}`} replace state={{ returnTo }} />;
  const readOnly = Boolean(operation.debtId) || workspace?.currentMembership.status !== 'ACTIVE' || !(canManageFinance(workspace.currentMembership.role) || operation.createdBy === user.id);
  const category = snapshot.categories.find(
    item => item.id === operation.categoryId,
  );
  const member = snapshot.members.find(
    item => item.id === operation.authorProjectionId,
  );
  const Icon =
    operation.type === 'TRANSFER'
      ? Repeat2
      : getFinanceIcon(
          category?.icon,
          operation.type === 'INCOME' ? 'coins' : 'shopping-bag',
        );
  const positive = operation.type === 'INCOME';
  const cancelled = operation.status === 'CANCELLED';
  const categoryName =
    operation.type === 'TRANSFER'
      ? 'Перевод'
      : category?.name ?? 'Без категории';
  const act = async (action: 'cancel' | 'restore') => {
    try {
      await apiMutation(
        `/workspaces/${workspace!.id}/operations/${operation.id}/${action}`,
        {
          scope: `operation:${action}:${operation.id}`,
          body:
            action === 'cancel'
              ? { reason: 'Отменено в Казна Web' }
              : undefined,
          version: operation.version,
        },
      );
      closeActions();
      await queryClient.invalidateQueries({
        predicate: query => query.queryKey.includes(workspace!.id),
      });
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 409) {
        await operationQuery.refetch();
        closeActions();
        window.alert(
          'Операция изменилась на другом устройстве. Данные обновлены.',
        );
      }
    }
  };
  const editReturnPath = `${location.pathname}${location.search.replace(
    /([?&])overlay=operation-actions(&|$)/u,
    '$1',
  ).replace(/[?&]$/u, '')}`;
  return (
    <Dialog
      title="Операция"
      onClose={() => navigate(returnTo, { replace: true })}
      trailing={!readOnly &&
        <button
          type="button"
          className="dialog-header-action destructive"
          aria-label="Действия с операцией"
          onClick={() => {
            const next = new URLSearchParams(searchParams);
            next.set('overlay', 'operation-actions');
            setSearchParams(next, { state: routeState });
          }}
        >
          <Trash2 size={20} />
        </button>
      }
    >
      <section className={`transaction-details ${cancelled ? 'cancelled' : ''}`}>
        <article className="transaction-details-hero">
          <span
            className="transaction-details-icon"
            style={
              {
                '--category-color': category?.color ?? 'var(--primary)',
              } as CSSProperties
            }
          >
            {createElement(Icon, { size: 35 })}
          </span>
          <h1>{categoryName}</h1>
          <strong className={positive ? 'positive' : ''}>
            {positive ? '+' : operation.type === 'EXPENSE' ? '−' : ''}
            {formatMoney(operation.amountMinor)}
          </strong>
          <span className={`transaction-status ${positive ? 'positive' : ''}`}>
            {cancelled
              ? 'Операция отменена'
              : positive
                ? 'Доход'
                : operation.type === 'TRANSFER'
                  ? 'Перевод'
                  : 'Расход сохранён'}
          </span>
        </article>
        <article className="transaction-details-info">
          <TransactionInfoRow
            icon={CalendarDays}
            label="Дата и время"
            value={formatCompactOperationDate(operation.occurredAt)}
          />
          <TransactionInfoRow
            icon={UserRound}
            label="Добавил"
            value={member?.name ?? 'Участник'}
          />
          <TransactionInfoRow
            icon={MessageCircle}
            label="Комментарий"
            value={operation.note ?? 'Без комментария'}
          />
        </article>
      </section>
      {operation.debtId && <p>Неизвестное старое движение доступно только для чтения.</p>}
      {showActions && !readOnly && (
        <Dialog
          variant="sheet"
          title="Действия с операцией"
          onClose={closeActions}
        >
          <div className="mobile-choice-list operation-action-sheet">
            {!cancelled && operation.type !== 'TRANSFER' && (
              <button
                type="button"
                onClick={() =>
                  navigate(
                    `/operations/${operation.id}/edit?returnPath=${encodeURIComponent(editReturnPath)}`,
                  )
                }
              >
                <span><Pencil size={21} /></span>
                <b>Редактировать</b>
                <ChevronRight size={20} />
              </button>
            )}
            <button
              type="button"
              className={cancelled ? '' : 'destructive'}
              onClick={() => void act(cancelled ? 'restore' : 'cancel')}
            >
              <span>
                {cancelled ? <RotateCcw size={21} /> : <Trash2 size={21} />}
              </span>
              <b>{cancelled ? 'Восстановить' : 'Отменить операцию'}</b>
              <ChevronRight size={20} />
            </button>
          </div>
        </Dialog>
      )}
    </Dialog>
  );
}

function TransactionInfoRow({
  icon: Icon,
  label,
  value,
}: {
  icon: LucideIcon;
  label: string;
  value: string;
}) {
  return (
    <div className="transaction-info-row">
      <span><Icon size={19} /></span>
      <div>
        <small>{label}</small>
        <strong>{value}</strong>
      </div>
    </div>
  );
}

function formatCompactOperationDate(value: string): string {
  return new Intl.DateTimeFormat('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
    .format(new Date(value))
    .replace(',', ' ·');
}

export function LimitsPage() {
  const { workspace } = useAppData();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const snapshotQuery = useSnapshot();
  const requestedMonth = searchParams.get('month');
  const initialMonth = /^\d{4}-\d{2}$/u.test(requestedMonth ?? '')
    ? requestedMonth!
    : todayKey().slice(0, 7);
  const monthDate = monthKeyToDate(initialMonth);
  const snapshot = snapshotQuery.data?.snapshot;
  if (snapshotQuery.isPending) return <Loading />;
  if (snapshot === undefined) return <ReadError />;
  const canManage = canManageFinance(
    workspace!.currentMembership.role,
  );
  const categories = snapshot.categories.filter(
    item =>
      item.type === 'expense' &&
      !item.system &&
      !item.parentId &&
      !item.archivedAt,
  );
  const setMonth = (date: Date) => {
    const next = new URLSearchParams(searchParams);
    next.set('month', dateToMonthKey(date));
    setSearchParams(next, { replace: true });
  };
  return (
    <Dialog
      title="Лимиты категорий"
      onClose={() => navigate('/account', { replace: true })}
    >
      <section className="category-limits-page">
        <div className="limits-month-banner">
          <Gauge size={21} />
          <button
            type="button"
            aria-label="Предыдущий месяц"
            onClick={() =>
              setMonth(
                new Date(
                  monthDate.getFullYear(),
                  monthDate.getMonth() - 1,
                  1,
                ),
              )
            }
          >
            <ChevronLeft size={20} />
          </button>
          <div>
            <small>ЛИМИТЫ НА МЕСЯЦ</small>
            <strong>{formatMonthKey(initialMonth)}</strong>
          </div>
          <button
            type="button"
            aria-label="Следующий месяц"
            onClick={() =>
              setMonth(
                new Date(
                  monthDate.getFullYear(),
                  monthDate.getMonth() + 1,
                  1,
                ),
              )
            }
          >
            <ChevronRight size={20} />
          </button>
        </div>
        <p className="limits-description">
          {canManage
            ? 'Нажмите на категорию, чтобы задать или изменить её месячный лимит.'
            : 'Лимиты доступны только для просмотра.'}
        </p>
        <div className="category-limit-list">
          {categories.map(category => {
            const spent = categorySpentForMonth(
              snapshot,
              category.id,
              initialMonth,
            );
            const limit =
              snapshot.categoryLimits.find(
                item =>
                  item.categoryId === category.id &&
                  item.month === initialMonth,
              )?.limit ?? category.limit;
            const remaining = limit - spent;
            const exceeded = limit > 0 && remaining < 0;
            const Icon = getFinanceIcon(category.icon, 'shopping-bag');
            return (
              <button
                type="button"
                className="category-limit-card"
                aria-label={`Настроить лимит: ${category.name}`}
                disabled={!canManage}
                key={category.id}
                onClick={() =>
                  navigate(`/limits/${category.id}/${initialMonth}`)
                }
              >
                <span style={{ background: category.color }}>
                  {createElement(Icon, { size: 22 })}
                </span>
                <div>
                  <header>
                    <strong>{category.name}</strong>
                    <span>{limit > 0 ? formatMoney(limit) : 'Без лимита'}</span>
                  </header>
                  <Progress
                    value={limit > 0 ? spent / limit : 0}
                    danger={exceeded}
                    color={category.color}
                  />
                  <footer>
                    <span>Потрачено {formatMoney(spent)}</span>
                    <span className={exceeded ? 'negative' : 'positive'}>
                      {limit > 0
                        ? exceeded
                          ? `Сверх ${formatMoney(Math.abs(remaining))}`
                          : `Осталось ${formatMoney(remaining)}`
                        : 'Задайте лимит'}
                    </span>
                  </footer>
                </div>
                <ChevronRight size={18} />
              </button>
            );
          })}
        </div>
      </section>
    </Dialog>
  );
}

export function EditLimitPage() {
  const { apiGet, apiMutation } = useAccountApi();
  const { workspace } = useAppData();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { categoryId, month } = useParams();
  const snapshotQuery = useSnapshot();
  const limitsQuery = useQuery({
    queryKey: ['finance', workspace?.id, 'category-limits', month],
    enabled:
      workspace !== undefined &&
      month !== undefined &&
      /^\d{4}-\d{2}$/u.test(month),
    queryFn: () =>
      apiGet<Envelope<CategoryLimit[]>>(
        `/workspaces/${workspace!.id}/category-limits?fromMonth=${month!}&toMonth=${month!}`,
      ).then(result => result.data),
  });
  const [amount, setAmount] = useState('');
  const [serverError, setServerError] = useState('');
  const [pending, setPending] = useState(false);
  if (snapshotQuery.isPending || limitsQuery.isPending) return <Loading />;
  const snapshot = snapshotQuery.data?.snapshot;
  const category = snapshot?.categories.find(
    item => item.id === categoryId && !item.archivedAt,
  );
  if (
    snapshot === undefined ||
    category === undefined ||
    month === undefined ||
    !/^\d{4}-\d{2}$/u.test(month)
  ) {
    return <ReadError />;
  }
  const existing = limitsQuery.data?.find(
    item => item.categoryId === category.id && item.month === month,
  );
  const currentLimit =
    snapshot.categoryLimits.find(
      item => item.categoryId === category.id && item.month === month,
    )?.limit ?? category.limit;
  const spent = categorySpentForMonth(snapshot, category.id, month);
  let parsedAmount = currentLimit;
  let amountValid = false;
  if (amount.trim()) {
    try {
      parsedAmount = rublesToMinor(amount);
      amountValid = true;
    } catch {
      amountValid = false;
    }
  }
  const previewRemaining = parsedAmount - spent;
  const Icon = getFinanceIcon(category.icon, 'shopping-bag');
  const close = () =>
    navigate(`/limits?month=${encodeURIComponent(month)}`, {
      replace: true,
    });
  const mutate = async (remove = false) => {
    if (!remove && !amountValid) return;
    if (remove && existing === undefined) {
      close();
      return;
    }
    setPending(true);
    setServerError('');
    try {
      const path = `/workspaces/${workspace!.id}/category-limits/${month}/${category.id}`;
      if (remove) {
        await apiMutation(path, {
          method: 'DELETE',
          scope: `limit:delete:${workspace!.id}:${category.id}:${month}`,
          version: existing!.version,
        });
      } else {
        await apiMutation(path, {
          method: 'PUT',
          scope: `limit:set:${workspace!.id}:${category.id}:${month}`,
          body: { limitMinor: parsedAmount },
          ...(existing === undefined
            ? { createOnly: true as const }
            : { version: existing.version }),
        });
      }
      await queryClient.invalidateQueries({
        predicate: query => query.queryKey.includes(workspace!.id),
      });
      close();
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 409) {
        await queryClient.invalidateQueries({
          predicate: query => query.queryKey.includes(workspace!.id),
        });
        setServerError(
          'Лимит изменён на другом устройстве. Данные обновлены — изменения не перезаписаны.',
        );
      } else {
        setServerError(
          caught instanceof Error
            ? caught.message
            : 'Не удалось сохранить лимит',
        );
      }
      setPending(false);
    }
  };
  return (
    <Dialog title="Лимит категории" onClose={close}>
      <section className="edit-limit-page">
        <article className="edit-limit-category">
          <span style={{ background: category.color }}>
            {createElement(Icon, { size: 23 })}
          </span>
          <div>
            <strong>{category.name}</strong>
            <small>{formatMonthKey(month)}</small>
          </div>
          <div>
            <small>Сейчас</small>
            <span>
              {currentLimit > 0 ? formatMoney(currentLimit) : 'Без лимита'}
            </span>
          </div>
        </article>
        <label className="operation-amount edit-limit-amount">
          <span>НОВЫЙ ЛИМИТ</span>
          <MoneyAmountInput
            inputMode="decimal"
            autoFocus
            maxLength={12}
            aria-label="Новый лимит"
            value={amount}
            valueForWidth={amount}
            onChange={event => {
              setAmount(event.target.value);
              setServerError('');
            }}
          />
        </label>
        <article
          className={`edit-limit-preview ${previewRemaining < 0 ? 'danger' : ''}`}
        >
          <Gauge size={21} />
          <div>
            <small>Уже потрачено {formatMoney(spent)}</small>
            <strong>
              {previewRemaining < 0
                ? `Превышение ${formatMoney(Math.abs(previewRemaining))}`
                : `Останется ${formatMoney(previewRemaining)}`}
            </strong>
          </div>
        </article>
        {serverError && <p className="error">{serverError}</p>}
        <button
          type="button"
          className="operation-save"
          disabled={!amountValid || pending}
          onClick={() => void mutate()}
        >
          <Check size={20} strokeWidth={3} />
          {pending ? 'Сохраняем…' : 'Сохранить лимит'}
        </button>
        <button
          type="button"
          className="edit-limit-remove"
          disabled={pending}
          onClick={() => void mutate(true)}
        >
          Убрать лимит
        </button>
      </section>
    </Dialog>
  );
}

export function ArchivePage() {
  const { apiGet, apiMutation } = useAccountApi();
  const { workspace } = useAppData();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const snapshotQuery = useSnapshot();
  const accounts = useQuery({
    queryKey: ['finance', workspace?.id, 'accounts'],
    enabled: workspace !== undefined,
    queryFn: () =>
      apiGet<Envelope<Account[]>>(
        `/workspaces/${workspace!.id}/accounts?includeArchived=true`,
      ).then(result => result.data),
  });
  const categories = useQuery({
    queryKey: ['finance', workspace?.id, 'categories'],
    enabled: workspace !== undefined,
    queryFn: () =>
      apiGet<Envelope<Category[]>>(
        `/workspaces/${workspace!.id}/categories?includeArchived=true`,
      ).then(result => result.data),
  });
  const requestedSection = searchParams.get('section');
  const section =
    requestedSection === 'budget' || requestedSection === 'expense' || requestedSection === 'debt'
      ? requestedSection
      : 'income';
  const [serverError, setServerError] = useState('');
  const snapshot = snapshotQuery.data?.snapshot;
  if (
    snapshotQuery.isPending ||
    accounts.isPending ||
    categories.isPending
  ) {
    return <Loading />;
  }
  if (snapshot === undefined) return <ReadError />;
  const archivedIncome =
    categories.data?.filter(
      item =>
        item.type === 'INCOME' &&
        !item.system &&
        !item.parentId &&
        Boolean(item.archivedAt),
    ) ?? [];
  const archivedExpense =
    categories.data?.filter(
      item =>
        item.type === 'EXPENSE' &&
        !item.system &&
        !item.parentId &&
        Boolean(item.archivedAt),
    ) ?? [];
  const archivedBudgets =
    accounts.data?.filter(item => Boolean(item.archivedAt)) ?? [];
  const items =
    section === 'income'
      ? archivedIncome
      : section === 'expense'
        ? archivedExpense
        : archivedBudgets;
  const canManage = canManageFinance(
    workspace!.currentMembership.role,
  );
  const restore = async (item: Category | Account) => {
    const kind = section === 'budget' ? 'accounts' : 'categories';
    setServerError('');
    try {
      await apiMutation(
        `/workspaces/${workspace!.id}/${kind}/${item.id}/restore`,
        {
          scope: `${kind}:restore:${item.id}`,
          version: item.version,
        },
      );
      await queryClient.invalidateQueries({
        predicate: query => query.queryKey.includes(workspace!.id),
      });
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 409) {
        await queryClient.invalidateQueries({
          predicate: query => query.queryKey.includes(workspace!.id),
        });
        setServerError(
          'Объект изменён на другом устройстве. Архив обновлён.',
        );
      } else {
        setServerError(
          caught instanceof Error
            ? caught.message
            : 'Не удалось восстановить',
        );
      }
    }
  };
  return (
    <Dialog
      title="Архив"
      onClose={() => navigate('/account', { replace: true })}
    >
      <section className="archive-page">
        <p>
          Операции архивных категорий и бюджетов остаются в истории и отчётах.
        </p>
        <div className="archive-segment">
          {[
            ['income', 'Доходы', archivedIncome.length],
            ['budget', 'Бюджеты', archivedBudgets.length],
            ['expense', 'Расходы', archivedExpense.length],
            ['debt', 'Долги', snapshot.debtCategories.filter(item => item.archivedAt).length],
          ].map(([value, label, count]) => (
            <button
              type="button"
              className={section === value ? 'active' : ''}
              aria-pressed={section === value}
              key={value}
              onClick={() => {
                const next = new URLSearchParams(searchParams);
                next.set('section', String(value));
                setSearchParams(next);
              }}
            >
              <span>{label}</span>
              {Number(count) > 0 && <b>{count}</b>}
            </button>
          ))}
        </div>
        {serverError && <p className="error">{serverError}</p>}
        {section === 'debt' ? <DebtArchiveList snapshot={snapshot} /> : <div className="archive-list">
          {items.length === 0 ? (
            <article className="archive-empty">
              <ArchiveRestore size={28} />
              <strong>Архив пуст</strong>
              <span>
                Здесь появятся архивные{' '}
                {section === 'income'
                  ? 'доходы'
                  : section === 'budget'
                    ? 'бюджеты'
                    : 'расходы'}
                .
              </span>
            </article>
          ) : (
            items.map(item => {
              const isAccount = 'ledgerBalanceMinor' in item;
              const color = isAccount ? item.color : item.color;
              const Icon = getFinanceIcon(
                item.icon ?? undefined,
                isAccount ? 'wallet' : 'shopping-bag',
              );
              const operationCount = isAccount
                ? snapshot.transactions.filter(
                    transaction => transaction.budgetId === item.id,
                  ).length
                : snapshot.transactions.filter(transaction => {
                    const family = new Set([
                      item.id,
                      ...snapshot.categories
                        .filter(category => category.parentId === item.id)
                        .map(category => category.id),
                    ]);
                    return family.has(transaction.categoryId);
                  }).length;
              return (
                <article className="archive-item" key={item.id}>
                  <span
                    style={
                      {
                        '--category-color': color,
                      } as CSSProperties
                    }
                  >
                    {createElement(Icon, { size: 23 })}
                  </span>
                  <div>
                    <strong>{item.name}</strong>
                    <small>
                      В архиве с {formatArchiveDate(item.archivedAt)}
                    </small>
                    <small>
                      {operationCount}{' '}
                      {operationCount === 1 ? 'операция' : 'операций'}
                      {isAccount
                        ? ` · ${formatMoney(item.ledgerBalanceMinor)}`
                        : ''}
                    </small>
                  </div>
                  {canManage && (
                    <button
                      type="button"
                      aria-label={`Восстановить: ${item.name}`}
                      onClick={() => void restore(item)}
                    >
                      <ArchiveRestore size={19} />
                    </button>
                  )}
                </article>
              );
            })
          )}
        </div>}
      </section>
    </Dialog>
  );
}

function monthKeyToDate(month: string): Date {
  const [year, monthNumber] = month.split('-').map(Number);
  return new Date(year, monthNumber - 1, 1);
}

function dateToMonthKey(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
}

function formatMonthKey(month: string): string {
  return new Intl.DateTimeFormat('ru-RU', {
    month: 'long',
    year: 'numeric',
  }).format(monthKeyToDate(month));
}

function categorySpentForMonth(
  snapshot: FinanceSnapshot,
  categoryId: string,
  month: string,
): number {
  const family = new Set([
    categoryId,
    ...snapshot.categories
      .filter(item => item.parentId === categoryId)
      .map(item => item.id),
  ]);
  return snapshot.transactions
    .filter(item => {
      const date = new Date(item.occurredAt);
      return (
        item.type === 'expense' &&
        family.has(item.categoryId) &&
        dateToMonthKey(date) === month
      );
    })
    .reduce((sum, item) => sum + item.amount, 0);
}

function formatArchiveDate(value: string | null | undefined): string {
  if (!value) return 'неизвестной даты';
  return new Intl.DateTimeFormat('ru-RU', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(new Date(value));
}

export const operationSchema = z
  .object({
    type: z.enum(['INCOME', 'EXPENSE', 'TRANSFER']),
    amount: z.string().min(1),
    accountId: z.string().min(1),
    targetAccountId: z.string().optional(),
    categoryId: z.string().optional(),
    note: z.string().max(500),
  })
  .superRefine((value, context) => {
    try { rublesToMinor(value.amount); } catch (error) {
      context.addIssue({ code: 'custom', path: ['amount'], message: (error as Error).message });
    }
    if (value.type === 'TRANSFER' && (!value.targetAccountId || value.targetAccountId === value.accountId)) {
      context.addIssue({ code: 'custom', path: ['targetAccountId'], message: 'Выберите другой счёт' });
    }
    if (value.type !== 'TRANSFER' && !value.categoryId) {
      context.addIssue({ code: 'custom', path: ['categoryId'], message: 'Выберите категорию' });
    }
  });
type OperationFormData = z.infer<typeof operationSchema>;

function StructureCreateForm({
  kind,
  category,
  account,
  parentId,
  initialAmountMinor,
  onDone,
}: {
  kind: 'budget' | 'income' | 'expense';
  category?: Category;
  account?: Account;
  parentId?: string;
  initialAmountMinor?: number;
  onDone: () => void;
}) {
  const { apiMutation } = useAccountApi();
  const { workspace } = useAppData();
  const queryClient = useQueryClient();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialName = category?.name ?? account?.name ?? '';
  const initialAmount =
    initialAmountMinor === undefined
      ? ''
      : String(initialAmountMinor / 100).replace('.', ',');
  const initialGoal = account?.profile.goalMinor
    ? String(account.profile.goalMinor / 100).replace('.', ',')
    : '';
  const initialIncludeInTotal =
    account?.profile.includeInTotal ?? (category?.includeInTotal !== false);
  const initialColor =
    account?.color ??
    category?.color ??
    (kind === 'budget'
      ? '#6366F1'
      : kind === 'income'
        ? '#10B981'
        : '#FB923C');
  const initialIcon = normalizeFinanceIconId(
    account?.icon ?? category?.icon,
    kind === 'budget'
      ? 'credit-card'
      : kind === 'income'
        ? 'coins'
        : 'shopping-bag',
  );
  const [name, setName] = useState(initialName);
  const [amount, setAmount] = useState(initialAmount);
  const [goal, setGoal] = useState(initialGoal);
  const [includeInTotal, setIncludeInTotal] = useState(
    initialIncludeInTotal,
  );
  const [color, setColor] = useState(initialColor);
  const [icon, setIcon] = useState(initialIcon);
  const [nameDirty, setNameDirty] = useState(false);
  const [amountDirty, setAmountDirty] = useState(false);
  const [goalDirty, setGoalDirty] = useState(false);
  const [includeInTotalDirty, setIncludeInTotalDirty] = useState(false);
  const [colorDirty, setColorDirty] = useState(false);
  const [iconDirty, setIconDirty] = useState(false);
  const showAppearance = searchParams.get('overlay') === 'appearance';
  const [saving, setSaving] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [error, setError] = useState('');
  const openAppearance = () => {
    const next = new URLSearchParams(searchParams);
    next.set('overlay', 'appearance');
    setSearchParams(next, {
      state: { ...(location.state as object | null), appearanceFromForm: true },
    });
  };
  const closeAppearance = () => {
    if ((location.state as { appearanceFromForm?: boolean } | null)?.appearanceFromForm) {
      navigate(-1);
      return;
    }
    const next = new URLSearchParams(searchParams);
    next.delete('overlay');
    setSearchParams(next, { replace: true, state: location.state });
  };
  const submit = async () => {
    if (!name.trim()) {
      setError(
        kind === 'budget'
          ? 'Введите название бюджета'
          : 'Введите название категории',
      );
      return;
    }
    setSaving(true);
    setError('');
    try {
      const dirty: StructureFormDirty = {
        name: nameDirty,
        amount: amountDirty,
        goal: goalDirty,
        includeInTotal: includeInTotalDirty,
        color: colorDirty,
        icon: iconDirty,
      };
      const values = { name, includeInTotal, color, icon };
      if (account !== undefined) {
        const goalMinor = goalDirty
          ? goal
            ? rublesToMinor(goal)
            : null
          : account.profile.goalMinor ?? null;
        if (goalMinor !== null && goalMinor <= 0) {
          throw new Error('Цель накопления должна быть больше нуля');
        }
        const update = buildBudgetUpdatePlan(
          account,
          values,
          dirty,
          amountDirty ? rublesToMinor(amount) : undefined,
          goalMinor,
        );
        if (update.target === 'none') {
          onDone();
          return;
        }
        if (update.target === 'profile') {
          await apiMutation(
            `/workspaces/${workspace!.id}/accounts/${account.id}/budget-profile`,
            {
              method: 'PATCH',
              scope: `budget-profile:update:${account.id}`,
              version: account.profile.version,
              body: update.body,
            },
          );
        } else {
          await apiMutation(
            `/workspaces/${workspace!.id}/accounts/${account.id}`,
            {
              method: 'PATCH',
              scope: `account:update:${account.id}`,
              version: account.version,
              body: update.body,
            },
          );
        }
      } else if (category !== undefined) {
        const categoryBody = buildCategoryUpdateBody(
          kind === 'income' ? 'income' : 'expense',
          values,
          dirty,
          amountDirty ? rublesToMinor(amount) : undefined,
        );
        if (Object.keys(categoryBody).length === 0) {
          onDone();
          return;
        }
        await apiMutation(
          `/workspaces/${workspace!.id}/categories/${category.id}`,
          {
            method: 'PATCH',
            scope: `category:update:${category.id}`,
            version: category.version,
            body: categoryBody,
          },
        );
      } else if (kind === 'budget') {
        const amountMinor = amount ? rublesToMinor(amount) : 0;
        const goalMinor = goal ? rublesToMinor(goal) : undefined;
        await apiMutation(`/workspaces/${workspace!.id}/accounts`, {
          scope: `account:create:${workspace!.id}`,
          body: {
            name: name.trim(),
            type: goalMinor ? 'GOAL' : 'OTHER',
            color,
            icon,
            budgetProfile: {
              type: goalMinor ? 'GOAL' : 'MONTHLY',
              periodLabel: goalMinor ? 'цель' : 'в месяц',
              limitMinor: amountMinor,
              goalMinor,
              includeInTotal,
              balanceAdjustmentMinor: 0,
            },
          },
        });
      } else {
        const amountMinor = amount ? rublesToMinor(amount) : 0;
        await apiMutation(`/workspaces/${workspace!.id}/categories`, {
          scope: `category:create:${workspace!.id}`,
          body: {
            ...(parentId === undefined ? {} : { parentId }),
            type: kind === 'income' ? 'INCOME' : 'EXPENSE',
            name: name.trim(),
            includeInTotal: kind === 'income' ? includeInTotal : true,
            color,
            icon,
            limitMinor:
              kind === 'expense' && parentId === undefined ? amountMinor : 0,
            amountMinor: kind === 'income' ? amountMinor : undefined,
          },
        });
      }
      await queryClient.invalidateQueries({
        predicate: query => query.queryKey.includes(workspace!.id),
      });
      onDone();
    } catch (caught) {
      if (
        caught instanceof ApiError &&
        (caught.status === 409 || caught.status === 412)
      ) {
        await queryClient.invalidateQueries({
          predicate: query => query.queryKey.includes(workspace!.id),
        });
        setError(
          `${kind === 'budget' ? 'Бюджет изменён' : 'Категория изменена'} на другом устройстве. Данные обновлены — откройте форму снова.`,
        );
      } else {
        setError(
          caught instanceof Error ? caught.message : 'Не удалось сохранить',
        );
      }
      setSaving(false);
    }
  };
  const archive = async () => {
    if (
      category === undefined ||
      !window.confirm(
        `Переместить «${category.name}» в архив? Все связанные операции сохранятся в истории.`,
      )
    ) {
      return;
    }
    setArchiving(true);
    setError('');
    try {
      await apiMutation(
        `/workspaces/${workspace!.id}/categories/${category.id}/archive`,
        {
          scope: `categories:archive:${category.id}`,
          version: category.version,
        },
      );
      await queryClient.invalidateQueries({
        predicate: query => query.queryKey.includes(workspace!.id),
      });
      onDone();
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 409) {
        await queryClient.invalidateQueries({
          predicate: query => query.queryKey.includes(workspace!.id),
        });
        setError(
          'Категория изменена на другом устройстве. Данные обновлены — откройте форму снова.',
        );
      } else {
        setError(
          caught instanceof Error
            ? caught.message
            : 'Не удалось переместить категорию в архив',
        );
      }
      setArchiving(false);
    }
  };
  const subtitle =
    kind === 'budget'
      ? undefined
      : kind === 'income'
        ? 'Категория доходов'
        : 'Категория расходов';
  return (
    <div className={`structure-mobile-form structure-kind-${kind}`}>
      {subtitle && <p className="structure-form-subtitle">{subtitle}</p>}
      <FinanceAppearancePicker icon={icon} color={color} onClick={openAppearance} />
      <FinanceNameField
        aria-label={kind === 'budget' ? 'Название бюджета' : 'Название категории'}
        value={name}
        onChange={event => { setName(event.target.value); setNameDirty(true); }}
        maxLength={30}
        placeholder={kind === 'budget' ? 'Например, Ремонт' : kind === 'income' ? 'Например, Подработка' : 'Например, Питомцы'}
      />
      {parentId === undefined && (
        <label className="mobile-form-field money-field">
          <span>
            {kind === 'budget'
              ? 'Сумма бюджета'
              : kind === 'income'
                ? 'Сумма на панели'
                : `Лимит на ${new Date()
                    .toLocaleDateString('ru-RU', {
                      month: 'long',
                      year: 'numeric',
                    })}`}
          </span>
          <span>
            <input
              aria-label={
                kind === 'income'
                  ? 'Сумма категории'
                  : kind === 'expense'
                    ? 'Лимит категории'
                    : 'Сумма бюджета'
              }
              inputMode="decimal"
              maxLength={12}
              value={amount}
              onChange={event => {
                setAmount(event.target.value);
                setAmountDirty(true);
              }}
              placeholder="0"
            />
            <b>₽</b>
          </span>
        </label>
      )}
      {kind === 'budget' && (
        <>
          <label className="mobile-form-field money-field">
            <span>Цель накопления (необязательно)</span>
            <span>
              <input
                aria-label="Цель накопления"
                inputMode="decimal"
                maxLength={12}
                value={goal}
                onChange={event => {
                  setGoal(event.target.value);
                  setGoalDirty(true);
                }}
                placeholder="0"
              />
              <b>₽</b>
            </span>
          </label>
          <p className="structure-form-hint">
            Прогресс считается от текущей суммы бюджета.
          </p>
        </>
      )}
      {kind !== 'expense' && (
        <label className="structure-switch-card">
          <span>
            <strong>Учитывать в общей сумме</strong>
            <small>
              {kind === 'budget'
                ? 'Бюджет останется на панели и будет доступен для переноса, но его сумма не войдёт в итог.'
                : 'Категория останется на панели, даже если её сумма не входит в итог.'}
            </small>
          </span>
          <input
            type="checkbox"
            role="switch"
            aria-label={
              kind === 'budget'
                ? 'Учитывать бюджет в общей сумме'
                : 'Учитывать категорию в общей сумме'
            }
            checked={includeInTotal}
            onChange={event => {
              setIncludeInTotal(event.target.checked);
              setIncludeInTotalDirty(true);
            }}
          />
        </label>
      )}
      {error && <p className="error">{error}</p>}
      <FinanceSaveButton
        type="button"
        disabled={saving || archiving}
        onClick={() => void submit()}
      >
        {saving
          ? 'Сохраняем…'
          : account !== undefined
            ? 'Сохранить бюджет'
            : category !== undefined
            ? 'Сохранить категорию'
          : kind === 'budget'
            ? 'Создать бюджет'
            : 'Создать категорию'}
      </FinanceSaveButton>
      {category !== undefined && (
        <button
          type="button"
          className="structure-archive"
          disabled={saving || archiving}
          onClick={() => void archive()}
        >
          <Archive size={19} />
          {archiving
            ? 'Перемещаем…'
            : `Переместить ${kind === 'income' ? 'доход' : 'расход'} в архив`}
        </button>
      )}
      {showAppearance && (
        <FinanceAppearanceEditor
          color={color}
          icon={icon}
          onColorChange={nextColor => {
            setColor(nextColor);
            setColorDirty(true);
          }}
          onIconChange={nextIcon => {
            setIcon(nextIcon);
            setIconDirty(true);
          }}
          onDone={closeAppearance}
        />
      )}
    </div>
  );
}

function OperationForm({
  budgets,
  categories,
  allowTransfer = false,
  operation,
  initial,
  onDone,
}: {
  budgets: FinanceSnapshot['budgets'];
  categories: FinanceSnapshot['categories'];
  allowTransfer?: boolean;
  operation?: Operation;
  initial?: OperationDraft;
  onDone: () => void;
}) {
  const { apiMutation } = useAccountApi();
  const { workspace } = useAppData();
  const queryClient = useQueryClient();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const snapshot = useSnapshot().data?.snapshot;
  const [serverError, setServerError] = useState('');
  const pickerParam = searchParams.get('picker');
  const budgetPicker =
    searchParams.get('overlay') === 'budget-picker' &&
    (pickerParam === 'accountId' || pickerParam === 'targetAccountId')
      ? pickerParam
      : undefined;
  const openBudgetPicker = (picker: 'accountId' | 'targetAccountId') => {
    const next = new URLSearchParams(searchParams);
    next.set('overlay', 'budget-picker');
    next.set('picker', picker);
    setSearchParams(next, { state: location.state });
  };
  const closeBudgetPicker = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('overlay');
    next.delete('picker');
    setSearchParams(next, { replace: true, state: location.state });
  };
  const [showSubcategoryForm, setShowSubcategoryForm] = useState(false);
  const [subcategoryName, setSubcategoryName] = useState('');
  const [creatingSubcategory, setCreatingSubcategory] = useState(false);
  const canManageStructure = canManageFinance(
    workspace!.currentMembership.role,
  );
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<OperationFormData>({
    resolver: zodResolver(operationSchema),
    defaultValues: operation === undefined
      ? {
          type: initial?.type ?? 'EXPENSE',
          amount: '',
          accountId: initial?.accountId ?? budgets[0]?.id ?? '',
          targetAccountId: initial?.targetAccountId,
          categoryId: initial?.categoryId,
          note: '',
        }
      : {
          type: operation.type,
          amount: String(operation.amountMinor / 100).replace('.', ','),
          accountId: operation.entries[0]?.accountId ?? budgets[0]?.id ?? '',
          categoryId: operation.categoryId ?? undefined,
          note: operation.note ?? '',
        },
  });
  const type = watch('type');
  const amountValue = watch('amount');
  const accountId = watch('accountId');
  const targetAccountId = watch('targetAccountId');
  const categoryId = watch('categoryId');
  const visibleCategories = categories.filter(
    item =>
      !item.system &&
      !item.archivedAt &&
      item.type === type.toLowerCase(),
  );
  const rootCategories = visibleCategories.filter(item => !item.parentId);
  const selectedCategory = visibleCategories.find(item => item.id === categoryId);
  const selectedRootId = selectedCategory?.parentId ?? selectedCategory?.id;
  const subcategories = visibleCategories.filter(item => item.parentId === selectedRootId);
  const selectedSource = budgets.find(item => item.id === accountId);
  const selectedTarget = budgets.find(item => item.id === targetAccountId);
  const budgetBalance = (budgetId: string) => {
    const budget = budgets.find(item => item.id === budgetId);
    if (budget === undefined || snapshot === undefined) return 0;
    const income = snapshot.transactions
      .filter(
        item =>
          item.budgetId === budgetId &&
          item.type === 'income' &&
          item.affectsAvailableBalance,
      )
      .reduce((sum, item) => sum + item.amount, 0);
    const expense = snapshot.transactions
      .filter(item => item.budgetId === budgetId && item.type === 'expense')
      .reduce((sum, item) => sum + item.amount, 0);
    return Math.max(
      0,
      budget.limit + (budget.balanceAdjustment ?? 0) + income - expense,
    );
  };
  const categoryAmount = (candidateId: string) => {
    if (snapshot === undefined) return 0;
    const family = new Set([
      candidateId,
      ...categories
        .filter(item => item.parentId === candidateId)
        .map(item => item.id),
    ]);
    const now = new Date();
    return snapshot.transactions
      .filter(item => {
        const date = new Date(item.occurredAt);
        return (
          item.type === type.toLowerCase() &&
          family.has(item.categoryId) &&
          date.getFullYear() === now.getFullYear() &&
          date.getMonth() === now.getMonth()
        );
      })
      .reduce((sum, item) => sum + item.amount, 0);
  };
  const categoryLimit = (candidateId: string, fallback: number) =>
    snapshot?.categoryLimits.find(
      item =>
        item.categoryId === candidateId &&
        item.month === todayKey().slice(0, 7),
    )?.limit ?? fallback;
  const addSubcategory = async () => {
    const selectedRoot = rootCategories.find(item => item.id === selectedRootId);
    if (selectedRoot === undefined || !subcategoryName.trim()) return;
    setCreatingSubcategory(true);
    setServerError('');
    try {
      const result = await apiMutation<Envelope<Category>>(
        `/workspaces/${workspace!.id}/categories`,
        {
          scope: `category:create:${workspace!.id}`,
          body: {
            parentId: selectedRoot.id,
            type: type,
            name: subcategoryName.trim(),
            includeInTotal: true,
            color: selectedRoot.color,
            icon: selectedRoot.icon,
            limitMinor: 0,
          },
        },
      );
      setValue('categoryId', result.data.id);
      setSubcategoryName('');
      setShowSubcategoryForm(false);
      await queryClient.invalidateQueries({
        predicate: query => query.queryKey.includes(workspace!.id),
      });
    } catch (caught) {
      setServerError(
        caught instanceof Error
          ? caught.message
          : 'Не удалось создать подкатегорию',
      );
    } finally {
      setCreatingSubcategory(false);
    }
  };
  const submit = async (values: OperationFormData) => {
    setServerError('');
    try {
      const amountMinor = rublesToMinor(values.amount);
      if (operation !== undefined) {
        await apiMutation(`/workspaces/${workspace!.id}/operations/${operation.id}`, {
          method: 'PATCH',
          scope: `operation:update:${operation.id}`,
          version: operation.version,
          body: {
            accountId: values.accountId,
            categoryId: values.categoryId,
            amountMinor,
            note: values.note || null,
          },
        });
      } else if (values.type === 'TRANSFER') {
        await apiMutation(`/workspaces/${workspace!.id}/transfers`, {
          scope: `transfer:create:${workspace!.id}`,
          body: {
            sourceAccountId: values.accountId,
            targetAccountId: values.targetAccountId,
            amountMinor,
            currency: 'RUB',
            occurredAt: new Date().toISOString(),
            note: values.note || undefined,
          },
        });
      } else {
        await apiMutation(`/workspaces/${workspace!.id}/operations`, {
          scope: `operation:create:${workspace!.id}`,
          body: {
            type: values.type,
            accountId: values.accountId,
            categoryId: values.categoryId,
            amountMinor,
            currency: 'RUB',
            occurredAt: new Date().toISOString(),
            note: values.note || undefined,
            affectsAvailableBalance: values.type === 'INCOME',
          },
        });
      }
      await queryClient.invalidateQueries({ predicate: query => query.queryKey.includes(workspace!.id) });
      onDone();
    } catch (error) {
      if (error instanceof ApiError && error.status === 409) {
        await queryClient.invalidateQueries({
          predicate: query => query.queryKey.includes(workspace!.id),
        });
        setServerError(
          'Операция изменена на другом устройстве. Данные обновлены — изменения не перезаписаны.',
        );
      } else {
        setServerError(
          error instanceof Error ? error.message : 'Не удалось сохранить',
        );
      }
    }
  };
  return (
    <form
      className="mobile-operation-form"
      onSubmit={event => void handleSubmit(submit)(event)}
    >
      {operation === undefined && type !== 'TRANSFER' && (
        <div className="operation-type-segment" aria-label="Тип операции">
          <button
            type="button"
            className={type === 'EXPENSE' ? 'active expense' : ''}
            onClick={() => {
              setValue('type', 'EXPENSE');
              setValue(
                'categoryId',
                categories.find(
                  item =>
                    item.type === 'expense' &&
                    !item.system &&
                    !item.archivedAt &&
                    !item.parentId,
                )?.id ?? '',
              );
            }}
          >
            <ArrowDownLeft size={18} /> Расход
          </button>
          <button
            type="button"
            className={type === 'INCOME' ? 'active income' : ''}
            onClick={() => {
              setValue('type', 'INCOME');
              setValue(
                'categoryId',
                categories.find(
                  item =>
                    item.type === 'income' &&
                    !item.system &&
                    !item.archivedAt &&
                    !item.parentId,
                )?.id ?? '',
              );
            }}
          >
            <ArrowUpRight size={18} /> Доход
          </button>
          {allowTransfer && (
            <button
              type="button"
              className="operation-transfer-shortcut"
              aria-label="Перевод"
              onClick={() => setValue('type', 'TRANSFER')}
            >
              <ArrowLeftRight size={18} />
            </button>
          )}
        </div>
      )}

      {type === 'TRANSFER' && (
        <section className="transfer-points-card">
          <BudgetPointButton
            budget={selectedSource}
            caption="Откуда"
            onClick={() => openBudgetPicker('accountId')}
          />
          <span className="transfer-arrow"><ArrowUpRight size={23} /></span>
          <BudgetPointButton
            budget={selectedTarget}
            caption="Куда"
            onClick={() => openBudgetPicker('targetAccountId')}
          />
        </section>
      )}

      <label className="operation-amount">
        <span>{type === 'TRANSFER' ? 'СУММА ПЕРЕВОДА' : 'СУММА'}</span>
        <MoneyAmountInput
          inputMode="decimal"
          autoFocus
          aria-label={type === 'TRANSFER' ? 'Сумма перевода' : 'Сумма операции'}
          valueForWidth={amountValue}
          {...register('amount')}
        />
        {errors.amount && <small className="error">{errors.amount.message}</small>}
      </label>

      {type !== 'TRANSFER' && (
        <div className={`operation-primary-selections ${type === 'INCOME' ? 'income' : ''}`}>
          <OperationSelectionCard title="Бюджет">
            <div className="operation-coin-grid">
              {budgets.map(budget => (
                <OperationCoin
                  key={budget.id}
                  name={budget.name}
                  amount={budgetBalance(budget.id)}
                  color={budget.accent}
                  icon={budget.icon}
                  iconFallback="wallet"
                  selected={accountId === budget.id}
                  onClick={() => setValue('accountId', budget.id)}
                />
              ))}
            </div>
          </OperationSelectionCard>
          <OperationSelectionCard title={type === 'INCOME' ? 'Доход' : 'Категория'}>
            <div className="operation-coin-grid">
              {rootCategories.map(category => {
                const amount = categoryAmount(category.id);
                const limit = categoryLimit(category.id, category.limit);
                const remaining = limit - amount;
                const exceeded = limit > 0 && remaining < 0;
                return (
                  <OperationCoin
                    key={category.id}
                    name={category.name}
                    amount={amount}
                    caption={
                      type === 'EXPENSE' && limit > 0
                        ? `${exceeded ? '−' : ''}${formatMoney(Math.abs(remaining))}`
                        : undefined
                    }
                    color={category.color}
                    icon={category.icon}
                    iconFallback={type === 'INCOME' ? 'coins' : 'shopping-bag'}
                    progress={
                      type === 'EXPENSE' && limit > 0
                        ? amount / limit
                        : undefined
                    }
                    danger={exceeded}
                    selected={selectedRootId === category.id}
                    onClick={() => setValue('categoryId', category.id)}
                  />
                );
              })}
            </div>
            {errors.categoryId && <small className="error">{errors.categoryId.message}</small>}
          </OperationSelectionCard>
        </div>
      )}

      {type !== 'TRANSFER' && selectedRootId && (
        <section className="operation-subcategories">
          <header>
            <div>
              <h3>Подкатегория</h3>
              <small>Необязательно</small>
            </div>
            {canManageStructure && (
              <button
                type="button"
                className="operation-subcategory-add"
                aria-label="Создать подкатегорию"
                onClick={() => setShowSubcategoryForm(value => !value)}
              >
                <Plus size={16} />
                Создать
              </button>
            )}
          </header>
          <div>
            <button
              type="button"
              className={categoryId === selectedRootId ? 'active' : ''}
              onClick={() => setValue('categoryId', selectedRootId)}
            >
              Без подкатегории
            </button>
            {subcategories.map(category => (
              <button
                type="button"
                key={category.id}
                className={categoryId === category.id ? 'active' : ''}
                onClick={() => setValue('categoryId', category.id)}
              >
                {category.name}
              </button>
            ))}
          </div>
          {canManageStructure && showSubcategoryForm && (
            <div className="operation-subcategory-form">
              <input
                autoFocus
                aria-label="Название подкатегории"
                value={subcategoryName}
                placeholder="Например, Супермаркет"
                onChange={event => setSubcategoryName(event.target.value)}
                onKeyDown={event => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    void addSubcategory();
                  }
                }}
              />
              <button
                type="button"
                aria-label="Сохранить подкатегорию"
                disabled={!subcategoryName.trim() || creatingSubcategory}
                onClick={() => void addSubcategory()}
              >
                <Check size={18} />
              </button>
            </div>
          )}
        </section>
      )}

      <OperationDetails
        transfer={type === 'TRANSFER'}
        noteInput={<input placeholder={type === 'TRANSFER' ? 'Комментарий к переводу' : 'Комментарий'} {...register('note')} />}
      />

      {serverError && <p className="error">{serverError}</p>}
      <FinanceSaveButton disabled={isSubmitting}>
        {isSubmitting
          ? 'Сохраняем…'
          : operation !== undefined
            ? 'Сохранить'
            : type === 'TRANSFER'
            ? 'Перевести'
            : `Добавить ${type === 'EXPENSE' ? 'расход' : 'доход'}`}
      </FinanceSaveButton>

      {budgetPicker && (
        <Dialog
          variant="sheet"
          title="Выберите бюджет"
          onClose={closeBudgetPicker}
        >
          <div className="mobile-choice-list">
            {budgets
              .filter(
                item =>
                  budgetPicker === 'accountId'
                    ? item.id !== targetAccountId
                    : item.id !== accountId,
              )
              .map(budget => {
                const selected =
                  (budgetPicker === 'accountId' ? accountId : targetAccountId) ===
                  budget.id;
                const Icon = getFinanceIcon(budget.icon);
                return (
                  <button
                    type="button"
                    key={budget.id}
                    onClick={() => {
                      setValue(budgetPicker, budget.id);
                      closeBudgetPicker();
                    }}
                  >
                    <span style={{ background: budget.accent }}>
                      <Icon size={22} />
                    </span>
                    <b>{budget.name}</b>
                    {selected && <Check size={21} />}
                  </button>
                );
              })}
          </div>
        </Dialog>
      )}
    </form>
  );
}

function MemberManager({ members, canManage, isOwner, onChanged }: { members: Member[]; canManage: boolean; isOwner: boolean; onChanged: () => Promise<void> }) {
  const { apiMutation } = useAccountApi();
  const { workspace, user } = useAppData();
  const change = async (member: Member, action: 'role' | 'remove' | 'owner') => {
    if (action === 'owner') {
      await apiMutation(`/workspaces/${workspace!.id}/ownership-transfer`, { scope: `ownership:${workspace!.id}`, body: { targetMemberId: member.id } });
    } else if (action === 'remove') {
      await apiMutation(`/workspaces/${workspace!.id}/members/${member.id}`, { method: 'DELETE', scope: `member:remove:${member.id}`, version: member.version });
    } else {
      await apiMutation(`/workspaces/${workspace!.id}/members/${member.id}`, { method: 'PATCH', scope: `member:role:${member.id}`, version: member.version, body: { role: member.role === 'ADMIN' ? 'MEMBER' : 'ADMIN' } });
    }
    await onChanged();
  };
  return <div className="stack-list member-list">{members.filter(item => item.status === 'ACTIVE').map(member => <div className="row-between" key={member.id}><div className="member-name"><span className="avatar" style={{ background: member.color }}>{member.initials}</span><div><strong>{member.displayName}</strong><small>{member.role}</small></div></div>{canManage && member.userId !== user.id && <div className="row-actions">{member.role !== 'OWNER' && <button className="button button-quiet" onClick={() => void change(member, 'role')}>{member.role === 'ADMIN' ? 'Сделать участником' : 'Сделать админом'}</button>}{isOwner && member.role !== 'OWNER' && <button className="button button-quiet" onClick={() => void change(member, 'owner')}>Передать владение</button>}{member.role !== 'OWNER' && <button className="icon-button" aria-label="Удалить участника" onClick={() => void change(member, 'remove')}><Trash2 size={16}/></button>}</div>}</div>)}</div>;
}

function DashboardVisibilitySection({
  title,
  kind,
  iconFallback,
  items,
  hidden,
  onChange,
}: {
  title: string;
  kind: 'category' | 'budget';
  iconFallback?: string;
  items: {
    id: string;
    name: string;
    icon?: string;
    color?: string;
    accent?: string;
  }[];
  hidden: string[];
  onChange: (ids: string[]) => void;
}) {
  return (
    <section className="dashboard-visibility-section">
      <h3>{title}</h3>
      <div className="dashboard-visibility-card">
        {items.map(item => {
          const visible = !hidden.includes(item.id);
          const Icon = getFinanceIcon(
            item.icon,
            iconFallback ?? (kind === 'budget' ? 'wallet' : 'other'),
          );
          return (
            <label key={`${kind}:${item.id}`} className="dashboard-visibility-row">
              <span
                className="dashboard-visibility-icon"
                style={{ background: item.color ?? item.accent ?? 'var(--primary)' }}
              >
                <Icon size={21} strokeWidth={2.3} />
              </span>
              <span>{item.name}</span>
              <input
                type="checkbox"
                role="switch"
                aria-label={`Показывать на панели: ${item.name}`}
                checked={visible}
                onChange={() =>
                  onChange(
                    visible
                      ? [...hidden, item.id]
                      : hidden.filter(id => id !== item.id),
                  )
                }
              />
            </label>
          );
        })}
        {items.length === 0 && <p className="muted dashboard-visibility-empty">Нет активных элементов</p>}
      </div>
    </section>
  );
}

function StructureRow({ name, archived, disabled, onEdit, onToggle }: { name: string; archived: boolean; disabled: boolean; onEdit: () => void; onToggle: () => void }) {
  return <div className={`row-between structure-row ${archived ? 'cancelled' : ''}`}><span>{name}</span><span className="row-actions"><button className="icon-button" disabled={disabled || archived} aria-label="Редактировать" onClick={onEdit}><Pencil size={16}/></button><button className="icon-button" disabled={disabled} aria-label={archived ? 'Восстановить' : 'Архивировать'} onClick={onToggle}>{archived ? <RotateCcw size={16}/> : <Archive size={16}/>}</button></span></div>;
}

function BudgetPointButton({
  budget,
  caption,
  onClick,
}: {
  budget?: FinanceSnapshot['budgets'][number];
  caption: string;
  onClick: () => void;
}) {
  return (
    <button type="button" className="budget-point" onClick={onClick}>
      <small>{caption}</small>
      <span style={{ background: budget?.accent ?? 'var(--surface-muted)' }}>
        {budget
          ? createElement(getFinanceIcon(budget.icon), { size: 27 })
          : <Plus size={25} />}
      </span>
      <strong>{budget?.name ?? 'Выберите'}</strong>
    </button>
  );
}

function OperationList({ operations, categories }: { operations: Operation[]; categories: { id: string; name: string }[] }) {
  const names = new Map(categories.map(item => [item.id, item.name]));
  return <div className="stack-list">{operations.map(operation => <div className="operation-row" key={operation.id}><OperationIcon type={operation.type}/><div><strong>{operation.categoryId ? names.get(operation.categoryId) : operationLabel(operation.type)}</strong><small>{new Date(operation.occurredAt).toLocaleDateString('ru-RU')}</small></div><b>{formatMoney(operation.amountMinor)}</b></div>)}{operations.length === 0 && <p className="muted">Пока нет операций</p>}</div>;
}

function OperationIcon({ type }: { type: Operation['type'] }) {
  return <span className={`operation-icon ${type.toLowerCase()}`}>{type === 'INCOME' ? <ArrowDownLeft size={18}/> : type === 'EXPENSE' ? <ArrowUpRight size={18}/> : <ArrowLeftRight size={18}/>}</span>;
}

function MetricCard({ label, value, tone, detail }: { label: string; value: string; tone?: string; detail?: string }) {
  return <article className={`metric-card ${tone ?? ''}`}><span>{label}</span><strong>{value}</strong>{detail && <small>{detail}</small>}</article>;
}

function Progress({ value, danger = false, color }: { value: number; danger?: boolean; color?: string }) {
  return <span className={`progress ${danger ? 'danger' : ''}`}><i style={{ width: `${Math.min(Math.max(value, 0), 1) * 100}%`, background: danger ? 'var(--negative)' : color }} /></span>;
}

function SettingsGroup({ title, children }: { title: string; children: React.ReactNode }) {
  return <section className="settings-group"><h2>{title}</h2><div>{children}</div></section>;
}

function SettingsLink({
  to,
  icon: Icon,
  label,
  subtitle,
  color = 'var(--primary)',
}: {
  to: string;
  icon: LucideIcon;
  label: string;
  subtitle?: string;
  color?: string;
}) {
  return (
    <Link
      className="settings-link"
      to={to}
      style={{ '--settings-color': color } as CSSProperties}
    >
      <span className="settings-link-icon"><Icon size={20} /></span>
      <span className="settings-link-copy">
        <strong>{label}</strong>
        {subtitle && <small>{subtitle}</small>}
      </span>
      <ChevronRight size={20}/>
    </Link>
  );
}

function settingsSectionTitle(section: string): string {
  const titles: Record<string, string> = {
    profile: 'Профиль',
    sessions: 'Сессии и устройства',
    workspaces: 'Пространства',
    participants: 'Участники',
    budgets: 'Счета и бюджеты',
    categories: 'Категории',
    limits: 'Лимиты',
    archive: 'Архив',
  };
  return titles[section] ?? 'Настройки';
}

function useOnlineStatus(): boolean {
  const [online, setOnline] = useState(() => navigator.onLine);
  useEffect(() => {
    const handleOnline = () => setOnline(true);
    const handleOffline = () => setOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);
  return online;
}

function Loading() { return <section className="empty-state"><div className="spinner"/><p>Загружаем данные…</p></section>; }
function ReadError() { return <section className="empty-state"><h2>Финансовые данные скрыты</h2><p>Проверьте подключение и обновите страницу.</p></section>; }
function operationLabel(type: Operation['type']) { return type === 'INCOME' ? 'Доход' : type === 'EXPENSE' ? 'Расход' : 'Перевод'; }
function formatPeriodTitle(anchor: string, period: Period): string {
  const date = new Date(`${anchor}T12:00:00`);
  if (period === 'DAY') {
    return new Intl.DateTimeFormat('ru-RU', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    }).format(date);
  }
  if (period === 'WEEK') {
    const start = new Date(date);
    const weekday = (start.getDay() + 6) % 7;
    start.setDate(start.getDate() - weekday);
    const end = new Date(start);
    end.setDate(end.getDate() + 6);
    return `${start.toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' })} — ${end.toLocaleDateString('ru-RU', { day: 'numeric', month: 'short', year: 'numeric' })}`;
  }
  if (period === 'YEAR') return String(date.getFullYear());
  return new Intl.DateTimeFormat('ru-RU', {
    month: 'long',
    year: 'numeric',
  }).format(date);
}
function shiftPeriodAnchor(anchor: string, period: Period, direction: -1 | 1) {
  const date = new Date(`${anchor}T12:00:00`);
  if (period === 'DAY') date.setDate(date.getDate() + direction);
  if (period === 'WEEK') date.setDate(date.getDate() + direction * 7);
  if (period === 'MONTH') date.setMonth(date.getMonth() + direction);
  if (period === 'YEAR') date.setFullYear(date.getFullYear() + direction);
  return [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, '0'),
    String(date.getDate()).padStart(2, '0'),
  ].join('-');
}
function platformName(platform: Session['platform']) { return platform === 'WEB' ? 'Браузер' : platform === 'IOS' ? 'iPhone / iPad' : 'Android'; }
